import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { 
  Users, Calendar, DollarSign, TrendingUp, LogOut, Home,
  Search, Plus, Mail, ChevronRight, UserPlus, ArrowLeft,
  Shield, ShieldCheck, Clock, Trash2, Unlock, FileText,
  AlertTriangle, Eye, Loader2, Download, Send, MapPin, Flag, UserX,
  CheckCircle, XCircle, UserCheck, MoreVertical, Ban, History, Crown, Tag, KeyRound, Camera
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { QRCodeCanvas } from 'qrcode.react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import AdminAnalyticsTab from '@/pages/admin/AdminAnalyticsTab';
import AdminMetricsTab from '@/pages/admin/AdminMetricsTab';
import AdminAuditLogTab from '@/pages/admin/AdminAuditLogTab';
import AdminManagementTab from '@/pages/admin/AdminManagementTab';
import TeamManagementTab from '@/pages/admin/TeamManagementTab';
import NotificationBell from '@/components/NotificationBell';
import EventPhotoUploader from '@/components/EventPhotoUploader';
import BillingHealthWidget from '@/components/BillingHealthWidget';
import StripePricingSetupWidget from '@/components/StripePricingSetupWidget';
import AdminMembersTab from '@/pages/admin/AdminMembersTab';
import AdminTestimonialsTab from '@/pages/admin/AdminTestimonialsTab';
import AdminReferralsTab from '@/pages/admin/AdminReferralsTab';
import AdminReportsTab from '@/pages/admin/AdminReportsTab';
import AdminEventsTab from '@/pages/admin/AdminEventsTab';

const CITIES = [
  "All Cities", "Los Angeles", "Porter Ranch", "San Diego", "San Francisco", "Bay Area", "Sacramento",
  "Orange County", "San Jose", "Santa Barbara", "Palm Springs", "Other California"
];

// Helper to get auth header — uses the member token
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function AdminPage() {
  const navigate = useNavigate();
  const { user: authUser, token: authToken, isAuthenticated, loading: authContextLoading } = useAuth();
  const [admin, setAdmin] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [circles, setCircles] = useState([]);
  const [events, setEvents] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [waitlistCityStats, setWaitlistCityStats] = useState([]);
  const [waitlistTotal, setWaitlistTotal] = useState(0);
  const [waitlistCityFilter, setWaitlistCityFilter] = useState('all');
  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Messaging State
  const [messageStats, setMessageStats] = useState(null);
  const [flaggedMessages, setFlaggedMessages] = useState([]);
  
  // Admin Management State (Super Admin)
  const [adminsList, setAdminsList] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [inviteAdminOpen, setInviteAdminOpen] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ email: '', firstName: '', lastName: '' });
  const [inviting, setInviting] = useState(false);
  
  // Audit Log Filters
  const [auditFilters, setAuditFilters] = useState({ action: '', adminId: '' });
  const [actionTypes, setActionTypes] = useState([]);

  // MFA State
  const [mfaStatus, setMfaStatus] = useState(null);
  const [mfaSetupData, setMfaSetupData] = useState(null);
  const [mfaVerifyCode, setMfaVerifyCode] = useState('');
  const [mfaBackupCodes, setMfaBackupCodes] = useState(null);
  const [mfaDisablePassword, setMfaDisablePassword] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  
  // Event RSVP Tracking State
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetailLoading, setEventDetailLoading] = useState(false);
  const [eventDetail, setEventDetail] = useState(null);
  const [addRsvpOpen, setAddRsvpOpen] = useState(false);
  const [addRsvpUserId, setAddRsvpUserId] = useState('');
  
  // Content Moderation State
  const [moderationStats, setModerationStats] = useState(null);
  const [flaggedContent, setFlaggedContent] = useState([]);
  const [contentReports, setContentReports] = useState([]);
  const [moderationActions, setModerationActions] = useState([]);
  const [moderationView, setModerationView] = useState('flagged'); // 'flagged', 'reports', 'actions'
  const [reviewingContent, setReviewingContent] = useState(null);
  const [moderationActionType, setModerationActionType] = useState('approve');
  const [addStrikeOnAction, setAddStrikeOnAction] = useState(false);
  const [moderationReason, setModerationReason] = useState('');
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [zipCodeFilter, setZipCodeFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');

  // Promo Apply Modal
  const [promoModal, setPromoModal] = useState({ open: false, member: null });
  const [memberPromoCode, setMemberPromoCode] = useState('');

  // Password Reset Modal
  const [resetModal, setResetModal] = useState({ open: false, member: null });
  const [resetEmailChoice, setResetEmailChoice] = useState('original'); // 'original' or 'custom'
  const [resetCustomEmail, setResetCustomEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  
  // Member Insights & Past-Due State
  const [insights, setInsights] = useState(null);
  const [pastDueAccounts, setPastDueAccounts] = useState([]);
  const [trialMembers, setTrialMembers] = useState([]);
  const [generatingPortal, setGeneratingPortal] = useState(null);
  
  // Account Management State
  const [accountActionModal, setAccountActionModal] = useState({ open: false, action: '', member: null });
  const [suspendDays, setSuspendDays] = useState('7');
  const [actionReason, setActionReason] = useState('');
  const [accountActionLoading, setAccountActionLoading] = useState(false);
  const [statusHistoryModal, setStatusHistoryModal] = useState({ open: false, member: null });
  const [statusHistory, setStatusHistory] = useState([]);
  
  // Modals
  const [createCircleOpen, setCreateCircleOpen] = useState(false);
  const [createEventOpen, setCreateEventOpen] = useState(false);
  const [editEventOpen, setEditEventOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [newCircleName, setNewCircleName] = useState('');
  const [newCircleCity, setNewCircleCity] = useState('');
  const [newCircleNotes, setNewCircleNotes] = useState('');
  const [circleDetailView, setCircleDetailView] = useState(null);
  const [circleDetail, setCircleDetail] = useState(null);
  const [circleRequests, setCircleRequests] = useState([]);
  const [circleEventModal, setCircleEventModal] = useState({ open: false, circleId: null });
  const [circleEventForm, setCircleEventForm] = useState({
    title: '', event_type: 'zoom', event_date: '', duration: '60 minutes',
    zoom_link: '', location_name: '', address: '', description: '', max_capacity: ''
  });
  const [creatingCircleEvent, setCreatingCircleEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '', city: '', event_date: '', location: '', max_capacity: 50, description: '', price: '', photo_url: '', member_discount_price: '',
    event_type: 'gathering', base_price: '', host_name: '', what_to_bring: '', registration_opens_at: '',
    waiver_template: '', children_welcome: false, requires_health_disclaimer: false, health_disclaimer: '',
    third_party_photography: false, practitioner_license_type: '', practitioner_license_number: '', practitioner_insurance_verified: false
  });

  // Guest Registration Modal
  const [guestModal, setGuestModal] = useState({ open: false, eventId: null, eventTitle: '' });
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestLoading, setGuestLoading] = useState(false);

  // Stat Detail Modal
  const [statDetail, setStatDetail] = useState({ open: false, title: '', members: [], loading: false });

  // Help Requests State
  const [helpRequests, setHelpRequests] = useState([]);
  const [helpReplyModal, setHelpReplyModal] = useState({ open: false, request: null });
  const [helpReplyText, setHelpReplyText] = useState('');
  const [helpReplySending, setHelpReplySending] = useState(false);
  const [helpUnreadCount, setHelpUnreadCount] = useState(0);

  // Delete Member State
  const [deleteMemberModal, setDeleteMemberModal] = useState({ open: false, member: null });
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteAction, setDeleteAction] = useState('soft'); // 'soft' or 'purge'
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // Bulk Delete State
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);
  const [bulkPattern, setBulkPattern] = useState('');
  const [bulkAction, setBulkAction] = useState('soft');
  const [bulkMatches, setBulkMatches] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState('');

  // Deleted Accounts View
  const [showingDeleted, setShowingDeleted] = useState(false);
  const [deletedMembers, setDeletedMembers] = useState([]);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [purgeAllModal, setPurgeAllModal] = useState(false);
  const [purgeAllConfirm, setPurgeAllConfirm] = useState('');
  const [purgeAllLoading, setPurgeAllLoading] = useState(false);

  // Stripe Audit
  const [stripeAuditModal, setStripeAuditModal] = useState(false);
  const [stripeAuditLoading, setStripeAuditLoading] = useState(false);
  const [stripeAuditData, setStripeAuditData] = useState(null);

  // Promo Code State
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoMembers, setPromoMembers] = useState([]);
  const [promoLoading, setPromoLoading] = useState(false);
  const [createPromoOpen, setCreatePromoOpen] = useState(false);
  const [newPromo, setNewPromo] = useState({ code: '', discount_type: 'free_trial', tier: 'thrive', duration_days: '10', percent_off: 10, applicable_plans: [], usage_limit: '50', expiry_date: '', label: '' });
  const [editingPromoId, setEditingPromoId] = useState(null);
  const [editingPromoUsage, setEditingPromoUsage] = useState(0);
  const [extendPromoModal, setExtendPromoModal] = useState({ open: false, member: null });
  const [extendDays, setExtendDays] = useState('10');

  // Marketing / QR Code State
  const [qrCodes, setQrCodes] = useState([]);
  const [qrLoading, setQrLoading] = useState(false);
  const [createQrOpen, setCreateQrOpen] = useState(false);
  const [newQr, setNewQr] = useState({ label: '', location: '', ref_code: '' });
  const [qrSignupsModal, setQrSignupsModal] = useState({ open: false, qr: null, members: [] });

  // Analytics State
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [checkInCode, setCheckInCode] = useState('');
  const [checkInResult, setCheckInResult] = useState(null);
  const [checkInLoading, setCheckInLoading] = useState(false);

  // Check admin authentication using member token
  useEffect(() => {
    if (authContextLoading) return; // Wait for AuthContext to finish loading

    if (!isAuthenticated || !authToken) {
      navigate('/login?redirect=/admin/dashboard');
      return;
    }

    if (!authUser?.is_admin) {
      toast.error('You do not have admin access.');
      navigate('/dashboard');
      return;
    }

    // Verify via /admin/me using the member token
    const checkAdmin = async () => {
      try {
        const response = await api.get('/admin/me', {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        setAdmin(response.data);
      } catch (error) {
        toast.error('Admin access denied.');
        navigate('/dashboard');
      } finally {
        setAuthLoading(false);
      }
    };

    checkAdmin();
  }, [authContextLoading, isAuthenticated, authToken, authUser, navigate]);

  const isSuperAdmin = admin?.role === 'super_admin';

  // Fetch data based on active tab
  const fetchData = useCallback(async () => {
    if (!admin) return;
    
    try {
      setLoading(true);
      const headers = getAuthHeader();
      
      if (activeTab === 'overview' || !stats) {
        const statsRes = await api.get('/admin/stats', { headers });
        setStats(statsRes.data);
        // Also fetch insights and past-due for overview
        try {
          const [insightsRes, pastDueRes, trialRes] = await Promise.all([
            api.get('/admin/insights', { headers }),
            api.get('/admin/past-due', { headers }),
            api.get('/admin/trial-members', { headers })
          ]);
          setInsights(insightsRes.data);
          setPastDueAccounts(pastDueRes.data.past_due_accounts || []);
          setTrialMembers(trialRes.data.trial_members || []);
        } catch (e) {
          console.error('Failed to fetch insights:', e);
        }
      }
      
      if (activeTab === 'members' || members.length === 0) {
        const params = new URLSearchParams();
        if (searchQuery) params.append('search', searchQuery);
        if (tierFilter && tierFilter !== 'all') params.append('tier', tierFilter);
        if (cityFilter && cityFilter !== 'All Cities') params.append('city', cityFilter);
        if (zipCodeFilter) params.append('zip_code', zipCodeFilter);
        if (paymentFilter && paymentFilter !== 'all') params.append('payment_status', paymentFilter);
        
        const membersRes = await api.get(`/admin/members?${params}`, { headers });
        setMembers(membersRes.data);
      }
      
      if (activeTab === 'circles') {
        const circlesRes = await api.get('/circles/admin/all', { headers });
        setCircles(circlesRes.data);
      }
      
      if (activeTab === 'circle-requests') {
        const reqsRes = await api.get('/circles/admin/requests', { headers });
        setCircleRequests(reqsRes.data.requests || []);
      }
      
      if (activeTab === 'events') {
        const eventsRes = await api.get('/admin/events', { headers });
        setEvents(eventsRes.data);
      }
      
      if (activeTab === 'waitlist') {
        const waitlistRes = await api.get('/admin/waitlist', { headers });
        setWaitlist(waitlistRes.data.waitlist || []);
        setWaitlistCityStats(waitlistRes.data.city_stats || []);
        setWaitlistTotal(waitlistRes.data.total_count || 0);
      }
      
      if (activeTab === 'revenue') {
        const revenueRes = await api.get('/admin/revenue', { headers });
        setRevenueData(revenueRes.data);
      }

      if (activeTab === 'analytics') {
        fetchAnalytics('30d');
      }

      // Super Admin only tabs
      if (activeTab === 'admin-management' && isSuperAdmin) {
        const adminsRes = await api.get('/admin/admins', { headers });
        setAdminsList(adminsRes.data.admins || []);
        fetchMfaStatus();
      }
      
      if (activeTab === 'audit-log' && isSuperAdmin) {
        const params = new URLSearchParams();
        if (auditFilters.action) params.append('action', auditFilters.action);
        if (auditFilters.adminId) params.append('admin_id', auditFilters.adminId);
        
        const [logsRes, actionsRes] = await Promise.all([
          api.get(`/admin/audit-log?${params}`, { headers }),
          api.get('/admin/audit-log/actions', { headers })
        ]);
        setAuditLogs(logsRes.data.logs || []);
        setActionTypes(actionsRes.data.actions || []);
      }
      
      if (activeTab === 'messages') {
        const [statsRes, flaggedRes] = await Promise.all([
          api.get('/admin/messages/stats', { headers }),
          api.get('/admin/messages/flagged', { headers })
        ]);
        setMessageStats(statsRes.data);
        setFlaggedMessages(flaggedRes.data || []);
      }
      
      if (activeTab === 'moderation') {
        const [statsRes, flaggedRes, reportsRes, actionsRes] = await Promise.all([
          api.get('/moderation/stats', { headers }),
          api.get('/moderation/flagged', { headers }),
          api.get('/moderation/reports', { headers }),
          api.get('/moderation/actions', { headers })
        ]);
        setModerationStats(statsRes.data);
        setFlaggedContent(flaggedRes.data.flagged_content || []);
        setContentReports(reportsRes.data.reports || []);
        setModerationActions(actionsRes.data.actions || []);
      }

      if (activeTab === 'promo-codes') {
        setPromoLoading(true);
        const [codesRes, membersRes] = await Promise.all([
          api.get('/promo/admin/codes', { headers }),
          api.get('/promo/admin/members', { headers })
        ]);
        setPromoCodes(codesRes.data || []);
        setPromoMembers(membersRes.data || []);
        setPromoLoading(false);
      }

      if (activeTab === 'marketing') {
        setQrLoading(true);
        const res = await api.get('/admin/qr-codes', { headers });
        setQrCodes(res.data || []);
        setQrLoading(false);
      }

      if (activeTab === 'help-requests') {
        const res = await api.get('/admin/help-requests', { headers });
        setHelpRequests(res.data?.requests || []);
      }

      // Always refresh unread count (cheap endpoint, drives the tab badge)
      try {
        const u = await api.get('/admin/help-requests/unread-count', { headers });
        setHelpUnreadCount(u.data?.count || 0);
      } catch { /* ignore */ }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired');
        navigate('/login?redirect=/admin/dashboard');
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  }, [activeTab, admin, isSuperAdmin, searchQuery, tierFilter, cityFilter, auditFilters, navigate, stats, members.length]);

  useEffect(() => {
    if (admin) {
      fetchData();
    }
  }, [admin, activeTab, fetchData]);

  const handleSearch = () => {
    setActiveTab('members');
    fetchData();
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    toast.success('Logged out successfully');
    navigate('/dashboard');
  };

  const handleCreateCircle = async () => {
    if (selectedMembers.length < 2 || selectedMembers.length > 8) {
      toast.error('Circle must have 2-8 members');
      return;
    }
    
    try {
      await api.post('/circles/admin/create', {
        name: newCircleName,
        city: newCircleCity,
        notes: newCircleNotes,
        member_ids: selectedMembers
      }, { headers: getAuthHeader() });
      toast.success('Circle created!');
      setCreateCircleOpen(false);
      setSelectedMembers([]);
      setNewCircleName('');
      setNewCircleCity('');
      setNewCircleNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create circle');
    }
  };

  const openCircleDetail = async (circleId) => {
    try {
      const res = await api.get(`/circles/admin/${circleId}`, { headers: getAuthHeader() });
      setCircleDetail(res.data);
      setCircleDetailView(circleId);
    } catch (error) {
      toast.error('Failed to load circle details');
    }
  };

  const handleDissolveCircle = async (circleId) => {
    if (!window.confirm('Are you sure? All members will be notified.')) return;
    try {
      await api.post(`/circles/admin/${circleId}/dissolve`, {}, { headers: getAuthHeader() });
      toast.success('Circle dissolved');
      setCircleDetailView(null);
      setCircleDetail(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to dissolve circle');
    }
  };

  const handleRemoveMemberFromCircle = async (circleId, userId) => {
    try {
      await api.post(`/circles/admin/${circleId}/remove-member`, { user_id: userId }, { headers: getAuthHeader() });
      toast.success('Member removed');
      openCircleDetail(circleId);
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const handleAddMemberToCircle = async (circleId, userId) => {
    try {
      await api.post(`/circles/admin/${circleId}/add-member`, { user_id: userId }, { headers: getAuthHeader() });
      toast.success('Member added and welcome email sent');
      openCircleDetail(circleId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  const handleCreateCircleEvent = async () => {
    if (!circleEventForm.title || !circleEventForm.event_date) {
      toast.error('Title and date are required');
      return;
    }
    setCreatingCircleEvent(true);
    try {
      await api.post(`/circles/admin/${circleEventModal.circleId}/events`, circleEventForm, { headers: getAuthHeader() });
      toast.success('Event created and members notified!');
      setCircleEventModal({ open: false, circleId: null });
      setCircleEventForm({ title: '', event_type: 'zoom', event_date: '', duration: '60 minutes', zoom_link: '', location_name: '', address: '', description: '', max_capacity: '' });
      if (circleDetailView) openCircleDetail(circleDetailView);
      fetchData();
    } catch (error) {
      toast.error('Failed to create event');
    } finally {
      setCreatingCircleEvent(false);
    }
  };

  const handleCancelCircleEvent = async (eventId) => {
    if (!window.confirm('Cancel this event? All members will be notified.')) return;
    try {
      await api.post(`/circles/admin/events/${eventId}/cancel`, {}, { headers: getAuthHeader() });
      toast.success('Event cancelled');
      if (circleDetailView) openCircleDetail(circleDetailView);
    } catch (error) {
      toast.error('Failed to cancel event');
    }
  };

  const handleAssignRequest = async (requestId, circleId) => {
    try {
      await api.post(`/circles/admin/requests/${requestId}/assign`, { circle_id: circleId }, { headers: getAuthHeader() });
      toast.success('Member assigned to circle and welcome email sent!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign');
    }
  };

  const handleWaitlistRequest = async (requestId) => {
    try {
      await api.post(`/circles/admin/requests/${requestId}/waitlist`, { note: 'Waitlisted by admin' }, { headers: getAuthHeader() });
      toast.success('Request waitlisted');
      fetchData();
    } catch (error) {
      toast.error('Failed to waitlist');
    }
  };

  const handleCreateEvent = async () => {
    try {
      const payload = { ...newEvent };
      if (payload.price) payload.price = parseFloat(payload.price);
      else payload.price = 0;
      if (payload.event_type === 'workshop') {
        payload.base_price = payload.base_price ? parseFloat(payload.base_price) : 0;
        if (payload.registration_opens_at) payload.registration_opens_at = new Date(payload.registration_opens_at).toISOString();
        else delete payload.registration_opens_at;
      } else {
        delete payload.base_price;
        delete payload.host_name;
        delete payload.registration_opens_at;
      }
      if (!payload.what_to_bring) delete payload.what_to_bring;
      if (payload.member_discount_price === '' || payload.member_discount_price == null) {
        payload.member_discount_price = null;
      } else {
        payload.member_discount_price = parseFloat(payload.member_discount_price);
      }
      if (!payload.photo_url) delete payload.photo_url;
      await api.post('/admin/events', payload, {
        headers: getAuthHeader()
      });
      toast.success('Event created!');
      setCreateEventOpen(false);
      setNewEvent({ title: '', city: '', event_date: '', location: '', max_capacity: 50, description: '', price: '', photo_url: '', member_discount_price: '', event_type: 'gathering', base_price: '', host_name: '', what_to_bring: '', registration_opens_at: '', waiver_template: '', children_welcome: false, requires_health_disclaimer: false, health_disclaimer: '', third_party_photography: false, practitioner_license_type: '', practitioner_license_number: '', practitioner_insurance_verified: false });
      fetchData();
    } catch (error) {
      toast.error('Failed to create event');
    }
  };

  const handleDeleteEvent = async (eventId, eventTitle) => {
    if (!window.confirm(`Delete "${eventTitle}"? This will remove all RSVPs and cannot be undone.`)) return;
    try {
      await api.delete(`/admin/events/${eventId}`, { headers: getAuthHeader() });
      toast.success(`Event "${eventTitle}" deleted`);
      setSelectedEvent(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    }
  };

  const handleRegisterGuest = async () => {
    if (!guestName.trim() || !guestModal.eventId) return;
    setGuestLoading(true);
    try {
      const res = await api.post(`/admin/events/${guestModal.eventId}/register-guest`, {
        name: guestName.trim(),
        email: guestEmail.trim() || undefined,
      }, { headers: getAuthHeader() });
      toast.success(res.data.message);
      setGuestModal({ open: false, eventId: null, eventTitle: '' });
      setGuestName('');
      setGuestEmail('');
      if (selectedEvent) fetchEventDetail(selectedEvent);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register guest');
    } finally {
      setGuestLoading(false);
    }
  };

  const handleStatClick = async (statType) => {
    const endpoints = {
      'Total Members': '/admin/stats/total-members',
      'Active Subscriptions': '/admin/stats/active-subscriptions',
      'New This Week': '/admin/stats/new-this-week',
      'Incomplete Onboarding': '/admin/stats/incomplete-onboarding',
    };
    if (statType === 'Monthly Revenue') {
      setActiveTab('analytics');
      return;
    }
    const url = endpoints[statType];
    if (!url) return;
    setStatDetail({ open: true, title: statType, members: [], loading: true });
    try {
      const res = await api.get(url, { headers: getAuthHeader() });
      setStatDetail({ open: true, title: statType, members: res.data.members || [], loading: false });
    } catch (error) {
      toast.error('Failed to load details');
      setStatDetail({ open: false, title: '', members: [], loading: false });
    }
  };

  const handleUnlinkPayment = async (userId, email) => {
    if (!window.confirm(`Unlink payment for ${email}? This will cancel their Stripe subscription.`)) return;
    try {
      const res = await api.post(`/admin/members/${userId}/unlink-payment`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlink');
    }
  };

  const handleUpdateHelpStatus = async (helpId, newStatus) => {
    try {
      await api.post(`/admin/help-requests/${helpId}/status`, { status: newStatus }, { headers: getAuthHeader() });
      toast.success('Status updated');
      setHelpRequests((prev) => prev.map((r) => (r.id === helpId ? { ...r, status: newStatus } : r)));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  // ===== Member Deletion =====
  const openDeleteMemberModal = (member) => {
    setDeleteMemberModal({ open: true, member });
    setDeleteConfirmText('');
    setDeleteAction('soft');
    setDeleteReason('');
  };

  const submitDeleteMember = async () => {
    const member = deleteMemberModal.member;
    if (!member) return;
    if (deleteConfirmText !== member.email) {
      toast.error("Type the member's email exactly to confirm");
      return;
    }
    setDeleteSubmitting(true);
    try {
      if (deleteAction === 'purge') {
        await api.delete(`/admin/members/${member.id}/purge`, { headers: getAuthHeader() });
        toast.success(`${member.first_name} ${member.last_name} permanently deleted`);
      } else {
        await api.post(`/admin/members/${member.id}/soft-delete`, { reason: deleteReason || undefined }, { headers: getAuthHeader() });
        toast.success(`${member.first_name} ${member.last_name} soft-deleted`);
      }
      setDeleteMemberModal({ open: false, member: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete member');
    }
    setDeleteSubmitting(false);
  };

  const runBulkPreview = async () => {
    if (!bulkPattern.trim() || bulkPattern.trim().length < 3) {
      toast.error('Pattern must be at least 3 characters');
      return;
    }
    setBulkLoading(true);
    setBulkMatches(null);
    try {
      const res = await api.post('/admin/members/bulk-delete/preview', { pattern: bulkPattern.trim() }, { headers: getAuthHeader() });
      setBulkMatches(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Preview failed');
    }
    setBulkLoading(false);
  };

  const submitBulkDelete = async () => {
    if (bulkConfirm !== bulkPattern) {
      toast.error('Type the pattern exactly to confirm');
      return;
    }
    setBulkLoading(true);
    try {
      const res = await api.post(
        '/admin/members/bulk-delete',
        { pattern: bulkPattern.trim(), action: bulkAction, confirm: bulkConfirm },
        { headers: getAuthHeader() }
      );
      toast.success(`${res.data.processed_count} processed, ${res.data.skipped_count} skipped`);
      setBulkDeleteModal(false);
      setBulkPattern(''); setBulkConfirm(''); setBulkMatches(null); setBulkAction('soft');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk delete failed');
    }
    setBulkLoading(false);
  };

  const fetchDeletedMembers = async () => {
    setDeletedLoading(true);
    try {
      const res = await api.get('/admin/members/deleted', { headers: getAuthHeader() });
      setDeletedMembers(res.data?.members || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load deleted members');
      setDeletedMembers([]);
    }
    setDeletedLoading(false);
  };

  const toggleDeletedView = async () => {
    const next = !showingDeleted;
    setShowingDeleted(next);
    if (next) {
      await fetchDeletedMembers();
    }
  };

  const handleRestoreMember = async (member) => {
    if (!window.confirm(`Restore ${member.first_name} ${member.last_name}? They will be able to log in again.`)) return;
    try {
      await api.post(`/admin/members/${member.id}/restore`, {}, { headers: getAuthHeader() });
      toast.success(`${member.first_name} ${member.last_name} restored`);
      setDeletedMembers((prev) => prev.filter((m) => m.id !== member.id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore');
    }
  };

  const handlePurgeDeletedMember = async (member) => {
    if (!window.confirm(`Permanently purge ${member.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/members/${member.id}/purge`, { headers: getAuthHeader() });
      toast.success(`${member.email} permanently purged`);
      setDeletedMembers((prev) => prev.filter((m) => m.id !== member.id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to purge');
    }
  };

  const submitPurgeAll = async () => {
    if (purgeAllConfirm !== 'PURGE ALL') {
      toast.error("Type 'PURGE ALL' exactly to confirm");
      return;
    }
    setPurgeAllLoading(true);
    try {
      const res = await api.post('/admin/members/purge-all-deleted', { confirm: purgeAllConfirm }, { headers: getAuthHeader() });
      toast.success(`${res.data.processed_count} purged, ${res.data.skipped_count} skipped`);
      setPurgeAllModal(false);
      setPurgeAllConfirm('');
      fetchDeletedMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purge-all failed');
    }
    setPurgeAllLoading(false);
  };

  const runStripeAudit = async () => {
    setStripeAuditModal(true);
    setStripeAuditLoading(true);
    setStripeAuditData(null);
    try {
      const res = await api.get('/stripe/admin/audit', { headers: getAuthHeader() });
      setStripeAuditData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Stripe audit failed');
      setStripeAuditModal(false);
    }
    setStripeAuditLoading(false);
  };

  const handleCancelOrphanSub = async (subscriptionId, customerEmail) => {
    if (!window.confirm(`Cancel Stripe subscription ${subscriptionId}${customerEmail ? ` for ${customerEmail}` : ''} immediately? This stops all future charges.`)) return;
    try {
      const res = await api.post(
        `/stripe/admin/cancel-subscription/${subscriptionId}`,
        { immediate: true },
        { headers: getAuthHeader() }
      );
      toast.success(`Subscription ${subscriptionId} cancelled (status: ${res.data.stripe_status})`);
      // Remove from local audit data
      setStripeAuditData((prev) => prev ? {
        ...prev,
        orphan_subs: prev.orphan_subs.filter((s) => s.stripe_subscription_id !== subscriptionId),
        free_tier_with_active_sub: prev.free_tier_with_active_sub.filter((s) => s.stripe_subscription_id !== subscriptionId),
        summary: {
          ...prev.summary,
          orphan_subs: prev.orphan_subs.filter((s) => s.stripe_subscription_id !== subscriptionId).length,
          free_tier_with_active_sub: prev.free_tier_with_active_sub.filter((s) => s.stripe_subscription_id !== subscriptionId).length,
        },
      } : prev);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cancellation failed');
    }
  };

  const openHelpReplyModal = async (request) => {
    setHelpReplyModal({ open: true, request });
    setHelpReplyText('');
    // Optimistic local unread count decrement
    if (request?.admin_unread) {
      setHelpUnreadCount((c) => Math.max(0, c - 1));
      setHelpRequests((prev) => prev.map((r) => (r.id === request.id ? { ...r, admin_unread: false } : r)));
    }
    // Fetch full thread (backend marks as read)
    try {
      const res = await api.get(`/admin/help-requests/${request.id}`, { headers: getAuthHeader() });
      setHelpReplyModal({ open: true, request: res.data });
    } catch { /* use summary */ }
  };

  const submitHelpReply = async () => {
    if (!helpReplyText.trim()) {
      toast.error('Please enter a reply');
      return;
    }
    setHelpReplySending(true);
    try {
      const res = await api.post(
        `/admin/help-requests/${helpReplyModal.request.id}/reply`,
        { message: helpReplyText.trim() },
        { headers: getAuthHeader() }
      );
      toast.success(res.data.email_sent ? 'Reply sent via email' : 'Reply saved (email not delivered)');
      setHelpReplyText('');
      // Refresh thread (keep modal open so admin can continue)
      try {
        const thread = await api.get(`/admin/help-requests/${helpReplyModal.request.id}`, { headers: getAuthHeader() });
        setHelpReplyModal({ open: true, request: thread.data });
      } catch { /* ignore */ }
      // Refresh list
      const list = await api.get('/admin/help-requests', { headers: getAuthHeader() });
      setHelpRequests(list.data?.requests || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reply');
    }
    setHelpReplySending(false);
  };

  const handleRelinkPayment = async (userId, email) => {
    try {
      const res = await api.post(`/admin/members/${userId}/relink-payment`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate relink');
    }
  };

  const handleInviteWaitlistMember = async (waitlistId, email) => {
    try {
      await api.post(`/admin/waitlist/${waitlistId}/invite`, {}, {
        headers: getAuthHeader()
      });
      toast.success(`Invitation sent to ${email}!`);
      fetchData(); // Refresh to update invited status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    }
  };

  const handleBulkInviteCity = async (city) => {
    if (!confirm(`Send invitations to all waitlist members in ${city}?`)) {
      return;
    }
    
    try {
      const response = await api.post('/admin/waitlist/bulk-invite', { city }, {
        headers: getAuthHeader()
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send bulk invitations');
    }
  };

  const handleExportWaitlist = async () => {
    try {
      const response = await api.get('/admin/waitlist/export', {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `calimoms_waitlist_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Waitlist exported successfully!');
    } catch (error) {
      toast.error('Failed to export waitlist');
    }
  };

  const handleExportMembers = async () => {
    try {
      const response = await api.get('/admin/members/export', {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `calimoms_members_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Members exported successfully!');
    } catch (error) {
      toast.error('Failed to export members');
    }
  };


  const handleDeleteWaitlistMember = async (waitlistId, email) => {
    if (!confirm(`Remove ${email} from the waitlist?`)) {
      return;
    }
    
    try {
      await api.delete(`/admin/waitlist/${waitlistId}`, {
        headers: getAuthHeader()
      });
      toast.success('Member removed from waitlist');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  // Filter waitlist by city
  const filteredWaitlist = waitlistCityFilter === 'all' 
    ? waitlist 
    : waitlist.filter(w => w.city === waitlistCityFilter);

  // Messaging Admin Functions
  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    try {
      await api.delete(`/admin/messages/${messageId}`, {
        headers: getAuthHeader()
      });
      toast.success('Message deleted');
      setFlaggedMessages(prev => prev.filter(m => m.id !== messageId));
      fetchData();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const handleSuspendMessaging = async (userId, userName) => {
    if (!window.confirm(`Suspend messaging privileges for ${userName}?`)) return;
    
    try {
      await api.post(`/admin/users/${userId}/suspend-messaging`, {}, {
        headers: getAuthHeader()
      });
      toast.success(`Messaging suspended for ${userName}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to suspend messaging');
    }
  };

  // Event RSVP Tracking Functions
  const fetchEventDetail = async (eventId) => {
    setEventDetailLoading(true);
    try {
      const response = await api.get(`/admin/events/${eventId}`, {
        headers: getAuthHeader()
      });
      setEventDetail(response.data);
      setSelectedEvent(eventId);
    } catch (error) {
      toast.error('Failed to load event details');
    } finally {
      setEventDetailLoading(false);
    }
  };

  const handleExportEventRsvps = async (eventId, eventTitle) => {
    try {
      const response = await api.get(`/admin/events/${eventId}/export`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${eventTitle.replace(/\s+/g, '_').toLowerCase()}_rsvps_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success('RSVP list exported');
    } catch (error) {
      toast.error('Failed to export RSVPs');
    }
  };

  const handleAddRsvp = async () => {
    if (!addRsvpUserId || !selectedEvent) return;
    
    try {
      await api.post(`/admin/events/${selectedEvent}/rsvp`, 
        { user_id: addRsvpUserId },
        { headers: getAuthHeader() }
      );
      toast.success('RSVP added');
      setAddRsvpOpen(false);
      setAddRsvpUserId('');
      fetchEventDetail(selectedEvent);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add RSVP');
    }
  };

  const handleRemoveRsvp = async (userId, userName) => {
    if (!window.confirm(`Remove ${userName}'s RSVP from this event?`)) return;
    
    try {
      await api.delete(`/admin/events/${selectedEvent}/rsvp/${userId}`, {
        headers: getAuthHeader()
      });
      toast.success('RSVP removed');
      fetchEventDetail(selectedEvent);
    } catch (error) {
      toast.error('Failed to remove RSVP');
    }
  };

  const handlePromoteFromWaitlist = async (userId, userName) => {
    if (!window.confirm(`Move ${userName} from waitlist to confirmed?`)) return;
    
    try {
      await api.post(`/admin/events/${selectedEvent}/promote-waitlist/${userId}`, {}, {
        headers: getAuthHeader()
      });
      toast.success(`${userName} has been confirmed and will receive an email`);
      fetchEventDetail(selectedEvent);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to promote from waitlist');
    }
  };

  // Event Payment Admin Functions
  const handleRefundPayment = async (eventId, paymentId, userName) => {
    if (!window.confirm(`Issue a full refund to ${userName}? This will also cancel their RSVP.`)) return;
    try {
      await api.post(`/events/admin/${eventId}/refund/${paymentId}`, {}, { headers: getAuthHeader() });
      toast.success(`Refund issued to ${userName}`);
      fetchEventDetail(eventId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Refund failed');
    }
  };

  const handleTransferCredit = async (eventId, paymentId, userName) => {
    if (!window.confirm(`Convert ${userName}'s ticket to event credit? Their RSVP will be removed and credit applied to their account for future events.`)) return;
    try {
      const res = await api.post(`/events/admin/${eventId}/transfer-credit/${paymentId}`, {}, { headers: getAuthHeader() });
      toast.success(`$${(res.data.credit_cents / 100).toFixed(2)} credit issued to ${userName}`);
      fetchEventDetail(eventId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Credit transfer failed');
    }
  };

  const getEventStatusBadge = (status) => {
    const styles = {
      'Upcoming': 'bg-green-100 text-green-700 border-green-300',
      'Full': 'bg-orange-100 text-orange-700 border-orange-300',
      'Past': 'bg-gray-100 text-gray-600 border-gray-300'
    };
    return styles[status] || 'bg-gray-100 text-gray-600';
  };

  // Analytics Functions
  const fetchAnalytics = async (period, startDate, endDate) => {
    setAnalyticsLoading(true);
    try {
      const p = period || '30d';
      let url = '/admin/analytics';
      if (p === 'custom' && startDate && endDate) {
        url += `?start_date=${startDate}&end_date=${endDate}`;
      } else if (p !== 'all') {
        url += `?period=${p}`;
      }
      const res = await api.get(url, { headers: getAuthHeader() });
      setAnalytics(res.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleCheckIn = async () => {
    if (!checkInCode.trim()) return;
    setCheckInLoading(true);
    setCheckInResult(null);
    try {
      const res = await api.post(`/events/admin/check-in/${checkInCode.trim()}`, {}, { headers: getAuthHeader() });
      setCheckInResult(res.data);
      if (res.data.status === 'checked_in') {
        toast.success(`${res.data.user_name} checked in!`);
      } else if (res.data.status === 'already_checked_in') {
        toast.info(`${res.data.user_name} was already checked in`);
      }
    } catch (error) {
      setCheckInResult({ status: 'error', message: error.response?.data?.detail || 'Invalid ticket' });
      toast.error(error.response?.data?.detail || 'Invalid ticket code');
    } finally {
      setCheckInLoading(false);
    }
  };

  // Content Moderation Functions
  const handleReviewContent = async (contentId, isReport = false) => {
    try {
      const endpoint = isReport 
        ? `/moderation/reports/${contentId}/review`
        : `/moderation/flagged/${contentId}/review`;
      
      await api.post(endpoint, {
        action: moderationActionType,
        reason: moderationReason,
        add_strike: addStrikeOnAction
      }, { headers: getAuthHeader() });
      
      toast.success(`Content ${moderationActionType}d successfully`);
      setReviewingContent(null);
      setModerationActionType('approve');
      setAddStrikeOnAction(false);
      setModerationReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to review content');
    }
  };

  const handleSuspendUserPosting = async (userId, days = 7) => {
    if (!window.confirm(`Suspend this user's posting privileges for ${days} days?`)) return;
    
    try {
      await api.post(`/moderation/users/${userId}/suspend`, 
        { days },
        { headers: getAuthHeader() }
      );
      toast.success('User posting privileges suspended');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend user');
    }
  };

  const handleUnsuspendUserPosting = async (userId) => {
    if (!window.confirm('Restore this user\'s posting privileges?')) return;
    
    try {
      await api.post(`/moderation/users/${userId}/unsuspend`, {}, { headers: getAuthHeader() });
      toast.success('User posting privileges restored');
      fetchData();
    } catch (error) {
      toast.error('Failed to restore privileges');
    }
  };

  const getSeverityBadge = (severity) => {
    const styles = {
      'high': 'bg-red-100 text-red-700 border-red-300',
      'medium': 'bg-orange-100 text-orange-700 border-orange-300',
      'low': 'bg-yellow-100 text-yellow-700 border-yellow-300'
    };
    return styles[severity] || 'bg-gray-100 text-gray-600';
  };

  // Admin Management Functions (Super Admin Only)
  const handleInviteAdmin = async () => {
    if (!newAdmin.email || !newAdmin.firstName || !newAdmin.lastName) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setInviting(true);
    try {
      await api.post('/admin/admins/invite', {
        email: newAdmin.email,
        first_name: newAdmin.firstName,
        last_name: newAdmin.lastName
      }, { headers: getAuthHeader() });
      
      toast.success(`Invitation sent to ${newAdmin.email}`);
      setInviteAdminOpen(false);
      setNewAdmin({ email: '', firstName: '', lastName: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite admin');
    } finally {
      setInviting(false);
    }
  };

  const handleRevokeAdmin = async (adminId, adminEmail) => {
    if (!confirm(`Are you sure you want to revoke admin access for ${adminEmail}?`)) {
      return;
    }
    
    try {
      await api.delete(`/admin/admins/${adminId}`, { headers: getAuthHeader() });
      toast.success('Admin access revoked');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke admin');
    }
  };

  const handleUnlockAdmin = async (adminId, adminEmail) => {
    try {
      await api.post(`/admin/admins/${adminId}/unlock`, {}, { headers: getAuthHeader() });
      toast.success(`Admin account ${adminEmail} unlocked`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock admin');
    }
  };

  // Pause / Unpause Membership
  const handlePauseMembership = async (member) => {
    if (!window.confirm(`Pause ${member.first_name} ${member.last_name}'s membership? They will be downgraded to free access.`)) return;
    try {
      await api.post(`/admin/members/${member.id}/pause`, {}, { headers: getAuthHeader() });
      toast.success(`${member.first_name}'s membership paused`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pause');
    }
  };

  const handleUnpauseMembership = async (member) => {
    if (!window.confirm(`Resume ${member.first_name} ${member.last_name}'s membership?`)) return;
    try {
      const res = await api.post(`/admin/members/${member.id}/unpause`, {}, { headers: getAuthHeader() });
      toast.success(`${member.first_name}'s membership resumed (${res.data.restored_tier})`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unpause');
    }
  };

  // Apply Promo to Member
  const handleApplyPromo = async () => {
    if (!memberPromoCode.trim() || !promoModal.member) return;
    setPromoLoading(true);
    try {
      const res = await api.post(`/admin/members/${promoModal.member.id}/apply-promo`, { code: memberPromoCode.trim() }, { headers: getAuthHeader() });
      toast.success(res.data.message);
      setPromoModal({ open: false, member: null });
      setMemberPromoCode('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply promo');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleSendResetLink = async () => {
    if (!resetModal.member) return;
    setResetLoading(true);
    try {
      const body = resetEmailChoice === 'custom' && resetCustomEmail.trim()
        ? { override_email: resetCustomEmail.trim() }
        : {};
      const res = await api.post(`/admin/members/${resetModal.member.id}/send-reset-link`, body, { headers: getAuthHeader() });
      toast.success(res.data.message);
      setResetModal({ open: false, member: null });
      setResetCustomEmail('');
      setResetEmailChoice('original');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset link');
    } finally {
      setResetLoading(false);
    }
  };

  // MFA Functions
  const fetchMfaStatus = async () => {
    try {
      const res = await api.get('/admin/mfa/status', { headers: getAuthHeader() });
      setMfaStatus(res.data);
    } catch { /* ignore */ }
  };

  const handleMfaSetup = async () => {
    setMfaLoading(true);
    try {
      const res = await api.post('/admin/mfa/setup', {}, { headers: getAuthHeader() });
      setMfaSetupData(res.data);
      setMfaVerifyCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start MFA setup');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleMfaVerify = async () => {
    if (!mfaVerifyCode.trim()) return;
    setMfaLoading(true);
    try {
      const res = await api.post('/admin/mfa/verify', { code: mfaVerifyCode.trim() }, { headers: getAuthHeader() });
      setMfaBackupCodes(res.data.backup_codes);
      setMfaSetupData(null);
      setMfaVerifyCode('');
      toast.success('MFA enabled successfully!');
      fetchMfaStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleMfaDisable = async () => {
    if (!mfaDisablePassword) {
      toast.error('Password is required to disable MFA');
      return;
    }
    if (!window.confirm('Disable two-factor authentication? This will make your account less secure.')) return;
    setMfaLoading(true);
    try {
      await api.post('/admin/mfa/disable', { password: mfaDisablePassword }, { headers: getAuthHeader() });
      toast.success('MFA disabled');
      setMfaDisablePassword('');
      setMfaBackupCodes(null);
      fetchMfaStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disable MFA');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleForceDisableMfa = async (adminId, email) => {
    if (!window.confirm(`Force-disable MFA for ${email}? They will be able to log in without a code.`)) return;
    try {
      await api.post(`/admin/mfa/force-disable/${adminId}`, {}, { headers: getAuthHeader() });
      toast.success(`MFA disabled for ${email}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const handleGeneratePortalLink = async (userId) => {
    setGeneratingPortal(userId);
    try {
      const res = await api.post(`/admin/members/${userId}/portal-link`, {}, { headers: getAuthHeader() });
      if (res.data.url) {
        navigator.clipboard.writeText(res.data.url);
        toast.success('Portal link copied to clipboard!');
      } else {
        toast.error(res.data.message || 'No Stripe customer ID on file');
      }
    } catch (error) {
      toast.error('Failed to generate portal link');
    } finally {
      setGeneratingPortal(null);
    }
  };

  const tierBadge = {
    free: 'bg-gray-100 text-gray-600',
    gather: 'bg-gray-100 text-gray-600',
    connect: 'bg-gray-100 text-gray-600',   // free tier under 2-tier model
    connected: 'bg-teal text-white',         // legacy paid alias → Thrive
    premium: 'bg-teal text-white',
    thrive: 'bg-teal text-white'
  };

  const tierLabel = (tier) => {
    const labels = {
      free: 'Connect',
      gather: 'Connect',
      connect: 'Connect',
      connected: 'Thrive',
      premium: 'Thrive',
      thrive: 'Thrive'
    };
    return labels[tier] || tier;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  // ===== Account Management Actions =====
  const openAccountAction = (action, member) => {
    setAccountActionModal({ open: true, action, member });
    setSuspendDays('7');
    setActionReason('');
  };

  const handleAccountAction = async () => {
    const { action, member } = accountActionModal;
    if (!member) return;
    setAccountActionLoading(true);
    try {
      const endpoint = `/admin/members/${member.id}/${action}`;
      const body = { reason: actionReason || `Account ${action} by admin` };
      if (action === 'suspend') {
        body.days = parseInt(suspendDays) || 7;
      }
      await api.post(endpoint, body, { headers: getAuthHeader() });
      toast.success(
        action === 'suspend' ? `Account suspended for ${body.days} days` :
        action === 'disable' ? 'Account disabled' :
        action === 'enable' ? 'Account re-enabled' :
        'Warning email sent'
      );
      setAccountActionModal({ open: false, action: '', member: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} account`);
    } finally {
      setAccountActionLoading(false);
    }
  };

  const openStatusHistory = async (member) => {
    setStatusHistoryModal({ open: true, member });
    try {
      const res = await api.get(`/admin/members/${member.id}/status-history`, { headers: getAuthHeader() });
      setStatusHistory(res.data.history || []);
    } catch (error) {
      toast.error('Failed to load status history');
      setStatusHistory([]);
    }
  };

  const getAccountStatusBadge = (member) => {
    const status = member.account_status || 'active';
    if (status === 'suspended') return { label: 'Suspended', className: 'bg-amber-100 text-amber-800 border-amber-300' };
    if (status === 'disabled') return { label: 'Disabled', className: 'bg-red-100 text-red-800 border-red-300' };
    return { label: 'Active', className: 'bg-green-100 text-green-800 border-green-300' };
  };

  const getPaymentStatusBadge = (member) => {
    if (member.membership_paused) return { label: 'Paused', className: 'bg-purple-100 text-purple-700 border-purple-300' };
    if (member.promo_status === 'active') return { label: 'Promo', className: 'bg-blue-100 text-blue-700 border-blue-300' };
    const tier = member.membership_tier || 'free';
    if (tier === 'free' || tier === 'gather' || tier === 'connect') return { label: 'Free', className: 'bg-gray-100 text-gray-600 border-gray-200' };
    const ps = member.payment_status;
    if (ps === 'failed') return { label: 'Past Due', className: 'bg-red-100 text-red-700 border-red-300' };
    if (ps === 'cancelled') return { label: 'Cancelled', className: 'bg-amber-100 text-amber-700 border-amber-300' };
    if (ps === 'paused') return { label: 'Paused', className: 'bg-purple-100 text-purple-700 border-purple-300' };
    return { label: 'Active', className: 'bg-green-100 text-green-700 border-green-300' };
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center">
        <div className="flex items-center gap-3 text-white">
          <Loader2 className="animate-spin" size={24} />
          <span>Verifying admin session...</span>
        </div>
      </div>
    );
  }

  if (!admin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-cream font-body">
      {/* Header */}
      <header className="bg-navy text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="font-display text-xl">
              Cali<span className="italic-teal">Moms</span> Admin
            </Link>
            {isSuperAdmin && (
              <Badge className="bg-amber-500 text-white">Super Admin</Badge>
            )}
          </div>
          <div className="flex items-center gap-4">
            <span className="text-white/70 text-sm hidden md:block">
              {admin.first_name} {admin.last_name}
            </span>
            <NotificationBell />
            <button 
              onClick={handleLogout} 
              className="text-white/70 hover:text-red-400 flex items-center gap-2"
              data-testid="admin-logout-btn"
            >
              <LogOut size={18} />
              <span className="hidden md:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8" data-testid="admin-main">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white rounded-xl p-1 mb-6 flex-wrap h-auto gap-1">
            <TabsTrigger value="overview" className="rounded-lg" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="members" className="rounded-lg" data-testid="tab-members">Members</TabsTrigger>
            <TabsTrigger value="messages" className="rounded-lg" data-testid="tab-messages">Messages</TabsTrigger>
            <TabsTrigger value="moderation" className="rounded-lg" data-testid="tab-moderation">
              <AlertTriangle size={14} className="mr-1" /> Moderation
            </TabsTrigger>
            <TabsTrigger value="circles" className="rounded-lg" data-testid="tab-circles">Circles</TabsTrigger>
            <TabsTrigger value="circle-requests" className="rounded-lg" data-testid="tab-circle-requests">Circle Requests</TabsTrigger>
            <TabsTrigger value="events" className="rounded-lg" data-testid="tab-events">Events</TabsTrigger>
            <TabsTrigger value="waitlist" className="rounded-lg" data-testid="tab-waitlist">Waitlist</TabsTrigger>
            <TabsTrigger value="revenue" className="rounded-lg" data-testid="tab-revenue">Revenue</TabsTrigger>
            <TabsTrigger value="promo-codes" className="rounded-lg" data-testid="tab-promo-codes">Promo Codes</TabsTrigger>
            <TabsTrigger value="marketing" className="rounded-lg" data-testid="tab-marketing">Marketing</TabsTrigger>
            {authUser?.is_admin && (
              <TabsTrigger value="testimonials" className="rounded-lg" data-testid="tab-testimonials">Testimonials</TabsTrigger>
            )}
            {authUser?.is_admin && (
              <TabsTrigger value="referrals" className="rounded-lg" data-testid="tab-referrals">Referrals</TabsTrigger>
            )}
            {authUser?.is_admin && (
              <TabsTrigger value="reports" className="rounded-lg" data-testid="tab-reports">Reports</TabsTrigger>
            )}
            <TabsTrigger value="analytics" className="rounded-lg" data-testid="tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="metrics" className="rounded-lg" data-testid="tab-metrics">Metrics</TabsTrigger>
            <TabsTrigger value="help-requests" className="rounded-lg relative" data-testid="tab-help-requests">
              Help Requests
              {helpUnreadCount > 0 && (
                <span
                  className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
                  data-testid="help-unread-badge"
                >
                  {helpUnreadCount > 9 ? '9+' : helpUnreadCount}
                </span>
              )}
            </TabsTrigger>
            {isSuperAdmin && (
              <>
                <TabsTrigger value="team-management" className="rounded-lg" data-testid="tab-team-management">
                  <Users size={14} className="mr-1" /> Team
                </TabsTrigger>
                <TabsTrigger value="admin-management" className="rounded-lg" data-testid="tab-admin-management">
                  <Shield size={14} className="mr-1" /> Admins
                </TabsTrigger>
                <TabsTrigger value="audit-log" className="rounded-lg" data-testid="tab-audit-log">
                  <FileText size={14} className="mr-1" /> Audit Log
                </TabsTrigger>
              </>
            )}
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" data-testid="admin-overview">
            {/* Quick Stats */}
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              {[
                { label: 'Total Members', value: stats?.total_members || 0, icon: Users, color: 'bg-teal' },
                { label: 'Active Subscriptions', value: stats?.active_subscriptions || 0, icon: UserPlus, color: 'bg-seafoam' },
                { label: 'Monthly Revenue', value: `$${stats?.monthly_revenue?.toFixed(2) || '0.00'}`, icon: DollarSign, color: 'bg-sand' },
                { label: 'New This Week', value: stats?.new_signups_this_week || 0, icon: TrendingUp, color: 'bg-navy' }
              ].map((stat, i) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white rounded-2xl p-6 shadow-card cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
                  onClick={() => handleStatClick(stat.label)}
                  data-testid={`stat-card-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center mb-4`}>
                    <stat.icon className="text-white" size={24} />
                  </div>
                  <p className="text-text-mid text-sm">{stat.label}</p>
                  <p className="text-3xl font-bold text-navy">{stat.value}</p>
                  <p className="text-xs text-teal mt-1">Click for details →</p>
                </motion.div>
              ))}
            </div>

            {/* Billing Health Widget + Pricing Setup */}
            <div className="mb-8 grid md:grid-cols-2 gap-4" data-testid="billing-health-section">
              <BillingHealthWidget getAuthHeader={getAuthHeader} />
              <StripePricingSetupWidget getAuthHeader={getAuthHeader} />
            </div>

            {/* Member Insights */}
            {insights && (
              <div className="mb-8" data-testid="member-insights-section">
                <h3 className="font-display text-lg text-navy mb-4">Member Insights</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {[
                    { label: 'Connect (Free)', value: (insights.members_by_tier?.free || 0) + (insights.members_by_tier?.gather || 0) + (insights.members_by_tier?.connect || 0), className: 'border-gray-200' },
                    { label: 'Thrive (Paid)', value: (insights.members_by_tier?.thrive || 0) + (insights.members_by_tier?.premium || 0) + (insights.members_by_tier?.connected || 0), className: 'border-teal/40' },
                    { label: 'New (30d)', value: insights.new_signups_30d || 0, className: 'border-green-200' },
                    { label: 'Payment Issues', value: insights.payment_issues || 0, className: insights.payment_issues > 0 ? 'border-red-300 bg-red-50/50' : 'border-gray-200' },
                    { label: 'Incomplete Onboarding', value: insights.incomplete_onboarding || 0, className: 'border-amber-200', clickable: true },
                    { label: 'Active Trials', value: insights.active_trials || 0, className: insights.active_trials > 0 ? 'border-teal/50 bg-teal/5' : 'border-gray-200' },
                  ].map((item, i) => (
                    <div key={i} className={`bg-white rounded-xl p-4 border ${item.className} ${item.clickable ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
                      onClick={() => item.clickable && handleStatClick('Incomplete Onboarding')}
                    >
                      <p className="text-xs text-text-mid mb-1">{item.label}</p>
                      <p className="text-2xl font-bold text-navy">{item.value}</p>
                      {item.clickable && <p className="text-xs text-teal mt-1">Click for details →</p>}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <p className="text-xs text-text-mid mb-1">Without Matches</p>
                    <p className="text-2xl font-bold text-navy">{insights.members_without_matches || 0}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <p className="text-xs text-text-mid mb-1">Pending Circle Requests</p>
                    <p className="text-2xl font-bold text-navy">{insights.pending_circle_requests || 0}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <p className="text-xs text-text-mid mb-1">Active Circles</p>
                    <p className="text-2xl font-bold text-navy">{insights.active_circles || 0}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Trial Members */}
            {trialMembers.length > 0 && (
              <div className="bg-white rounded-2xl shadow-card overflow-hidden mb-8" data-testid="trial-members-section">
                <div className="p-4 border-b border-border-subtle flex items-center gap-2">
                  <Clock size={18} className="text-teal" />
                  <h3 className="font-semibold text-navy">Active Trials ({trialMembers.length})</h3>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Trial Start</TableHead>
                      <TableHead>Trial End</TableHead>
                      <TableHead>Days Remaining</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trialMembers.map(m => (
                      <TableRow key={m.id}>
                        <TableCell className="font-medium">{m.first_name} {m.last_name}</TableCell>
                        <TableCell className="text-sm">{m.email}</TableCell>
                        <TableCell className="text-sm">{m.trial_start ? new Date(m.trial_start).toLocaleDateString() : '—'}</TableCell>
                        <TableCell className="text-sm">{m.trial_end ? new Date(m.trial_end).toLocaleDateString() : '—'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-teal/10 text-teal border-teal/30 text-xs">
                            {m.days_remaining} days
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Past-Due Accounts */}
            {pastDueAccounts.length > 0 && (
              <div className="bg-white rounded-2xl shadow-card overflow-hidden" data-testid="past-due-section">
                <div className="p-4 border-b border-border-subtle flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle size={18} className="text-red-500" />
                    <h3 className="font-semibold text-navy">Past-Due Accounts ({pastDueAccounts.length})</h3>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Tier</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Days Past Due</TableHead>
                      <TableHead>Stage</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pastDueAccounts.map(acct => (
                      <TableRow key={acct.id} data-testid={`past-due-row-${acct.id}`}>
                        <TableCell className="font-medium">{acct.first_name} {acct.last_name}</TableCell>
                        <TableCell className="text-sm">{acct.email}</TableCell>
                        <TableCell>
                          <Badge className={tierBadge[acct.membership_tier] || 'bg-gray-100 text-gray-600'}>
                            {tierLabel(acct.membership_tier)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            acct.payment_status === 'failed' ? 'bg-red-100 text-red-700 border-red-300' :
                            acct.payment_status === 'cancelled' ? 'bg-amber-100 text-amber-700 border-amber-300' :
                            'bg-gray-100 text-gray-600 border-gray-200'
                          }>
                            {acct.payment_status === 'failed' ? 'Payment Failed' :
                             acct.payment_status === 'cancelled' ? 'Cancelled' :
                             acct.payment_status === 'downgraded' ? 'Downgraded' : acct.payment_status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className={acct.days_past_due >= 7 ? 'text-red-600 font-semibold' : 'text-amber-600'}>
                            {acct.days_past_due} days
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-text-mid capitalize">{acct.reminder_stage || 'none'}</span>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleGeneratePortalLink(acct.id)}
                            disabled={generatingPortal === acct.id || !acct.stripe_customer_id}
                            className="rounded-full text-xs"
                            data-testid={`portal-link-${acct.id}`}
                          >
                            {generatingPortal === acct.id ? (
                              <Loader2 size={12} className="animate-spin mr-1" />
                            ) : (
                              <DollarSign size={12} className="mr-1" />
                            )}
                            {acct.stripe_customer_id ? 'Copy Portal Link' : 'No Stripe ID'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          {/* Members Tab */}
          <TabsContent value="members" data-testid="admin-members">
            <AdminMembersTab
              members={members}
              cities={CITIES}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              tierFilter={tierFilter}
              setTierFilter={setTierFilter}
              cityFilter={cityFilter}
              setCityFilter={setCityFilter}
              zipCodeFilter={zipCodeFilter}
              setZipCodeFilter={setZipCodeFilter}
              paymentFilter={paymentFilter}
              setPaymentFilter={setPaymentFilter}
              handleSearch={handleSearch}
              handleExportMembers={handleExportMembers}
              openAccountAction={openAccountAction}
              openStatusHistory={openStatusHistory}
              handlePauseMembership={handlePauseMembership}
              handleUnpauseMembership={handleUnpauseMembership}
              handleUnlinkPayment={handleUnlinkPayment}
              handleRelinkPayment={handleRelinkPayment}
              setPromoModal={setPromoModal}
              setMemberPromoCode={setMemberPromoCode}
              setResetModal={setResetModal}
              setResetEmailChoice={setResetEmailChoice}
              setResetCustomEmail={setResetCustomEmail}
              openDeleteMemberModal={openDeleteMemberModal}
              openBulkDeleteModal={() => setBulkDeleteModal(true)}
              showingDeleted={showingDeleted}
              toggleDeletedView={toggleDeletedView}
              deletedMembers={deletedMembers}
              deletedLoading={deletedLoading}
              handleRestoreMember={handleRestoreMember}
              handlePurgeDeletedMember={handlePurgeDeletedMember}
              openPurgeAllModal={() => setPurgeAllModal(true)}
              runStripeAudit={runStripeAudit}
              getAuthHeader={getAuthHeader}
              fetchData={fetchData}
              isMainAdmin={authUser?.is_admin === true}
            />
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" data-testid="admin-messages">
            <h2 className="font-display text-2xl text-navy mb-6">Messages</h2>
            
            {/* Stats Cards */}
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-2xl p-6 shadow-card">
                <p className="text-text-mid text-sm">Total Messages</p>
                <p className="text-3xl font-bold text-navy">{messageStats?.total_messages || 0}</p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-card">
                <p className="text-text-mid text-sm">Last 24 Hours</p>
                <p className="text-3xl font-bold text-teal">{messageStats?.messages_last_24h || 0}</p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-card">
                <p className="text-text-mid text-sm">Flagged Messages</p>
                <p className="text-3xl font-bold text-red-500">{messageStats?.flagged_messages || 0}</p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-card">
                <p className="text-text-mid text-sm">Active Conversations</p>
                <p className="text-3xl font-bold text-seafoam">{messageStats?.most_active_conversations?.length || 0}</p>
              </div>
            </div>

            {/* Most Active Conversations */}
            {messageStats?.most_active_conversations?.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-card mb-8">
                <h3 className="font-semibold text-navy mb-4">Most Active Conversations</h3>
                <div className="space-y-3">
                  {messageStats.most_active_conversations.map((conv, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-cream/50 rounded-xl">
                      <span className="font-medium text-navy">{conv.users}</span>
                      <div className="flex items-center gap-4 text-sm text-text-mid">
                        <span>{conv.message_count} messages</span>
                        <span>Last: {new Date(conv.last_message).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Flagged Messages */}
            <div className="bg-white rounded-2xl p-6 shadow-card">
              <h3 className="font-semibold text-navy mb-4 flex items-center gap-2">
                <Flag className="text-red-500" size={20} />
                Flagged Messages ({flaggedMessages.length})
              </h3>
              
              {flaggedMessages.length === 0 ? (
                <p className="text-center text-text-mid py-8">No flagged messages to review</p>
              ) : (
                <div className="space-y-4">
                  {flaggedMessages.map(msg => (
                    <div key={msg.id} className="border border-red-200 rounded-xl p-4 bg-red-50/30">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-semibold text-navy">
                            From: {msg.sender?.first_name} {msg.sender?.last_name}
                            <span className="text-text-mid font-normal ml-2">({msg.sender?.email})</span>
                          </p>
                          <p className="text-sm text-text-mid">
                            To: {msg.recipient?.first_name} {msg.recipient?.last_name}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-red-600 border-red-300">
                          Flagged {new Date(msg.flagged_at).toLocaleDateString()}
                        </Badge>
                      </div>
                      
                      <div className="bg-white p-3 rounded-lg border border-border-subtle mb-3">
                        <p className="text-navy">{msg.body}</p>
                        <p className="text-xs text-text-light mt-2">
                          Sent: {new Date(msg.sent_at).toLocaleString()}
                        </p>
                      </div>
                      
                      {msg.flagged_reason && (
                        <p className="text-sm text-red-600 mb-3">
                          <strong>Reason:</strong> {msg.flagged_reason}
                        </p>
                      )}
                      
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteMessage(msg.id)}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          <Trash2 size={14} className="mr-1" /> Delete Message
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSuspendMessaging(msg.sender_id, `${msg.sender?.first_name} ${msg.sender?.last_name}`)}
                          className="text-orange-600 border-orange-300 hover:bg-orange-50"
                        >
                          <UserX size={14} className="mr-1" /> Suspend User's Messaging
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Content Moderation Tab */}
          <TabsContent value="moderation" data-testid="admin-moderation">
            {/* Moderation Stats */}
            {moderationStats && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-xl p-4 border border-border-subtle">
                  <p className="text-sm text-navy/60">Pending Review</p>
                  <p className="text-2xl font-display text-navy">{moderationStats.flagged_content?.pending_review || 0}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-red-200">
                  <p className="text-sm text-red-600">High Severity</p>
                  <p className="text-2xl font-display text-red-600">{moderationStats.flagged_content?.high_severity || 0}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-border-subtle">
                  <p className="text-sm text-navy/60">Reports Pending</p>
                  <p className="text-2xl font-display text-navy">{moderationStats.reports?.pending || 0}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-orange-200">
                  <p className="text-sm text-orange-600">Users with Strikes</p>
                  <p className="text-2xl font-display text-orange-600">{moderationStats.users?.with_strikes || 0}</p>
                </div>
              </div>
            )}
            
            {/* View Selector */}
            <div className="flex gap-2 mb-4">
              <Button 
                variant={moderationView === 'flagged' ? 'default' : 'outline'}
                onClick={() => setModerationView('flagged')}
                className="rounded-full"
              >
                <AlertTriangle size={14} className="mr-2" /> AI Flagged ({flaggedContent.filter(f => !f.reviewed).length})
              </Button>
              <Button 
                variant={moderationView === 'reports' ? 'default' : 'outline'}
                onClick={() => setModerationView('reports')}
                className="rounded-full"
              >
                <Flag size={14} className="mr-2" /> Reports ({contentReports.filter(r => r.status === 'pending').length})
              </Button>
              <Button 
                variant={moderationView === 'actions' ? 'default' : 'outline'}
                onClick={() => setModerationView('actions')}
                className="rounded-full"
              >
                <FileText size={14} className="mr-2" /> Action Log
              </Button>
            </div>

            {/* Flagged Content View */}
            {moderationView === 'flagged' && (
              <div className="bg-white rounded-xl border border-border-subtle overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Severity</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Content Preview</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {flaggedContent.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-navy/60">
                          No flagged content to review
                        </TableCell>
                      </TableRow>
                    ) : (
                      flaggedContent.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <Badge className={getSeverityBadge(item.severity)}>
                              {item.severity?.toUpperCase() || 'N/A'}
                            </Badge>
                          </TableCell>
                          <TableCell className="capitalize">{item.content_type?.replace('_', ' ')}</TableCell>
                          <TableCell className="capitalize">{item.category?.replace('_', ' ')}</TableCell>
                          <TableCell>
                            {item.user?.first_name} {item.user?.last_name}
                          </TableCell>
                          <TableCell className="max-w-xs truncate" title={item.content_preview}>
                            {item.content_preview?.substring(0, 50)}...
                          </TableCell>
                          <TableCell>
                            {item.reviewed ? (
                              <Badge className="bg-green-100 text-green-700">Reviewed</Badge>
                            ) : (
                              <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {!item.reviewed && (
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => setReviewingContent(item)}
                                  >
                                    <Eye size={14} className="mr-1" /> Review
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>Review Flagged Content</DialogTitle>
                                    <DialogDescription>
                                      Review this content and take appropriate action
                                    </DialogDescription>
                                  </DialogHeader>
                                  <div className="space-y-4">
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                      <p className="text-sm font-medium text-navy/60 mb-2">Content:</p>
                                      <p className="text-sm">{item.content_preview}</p>
                                    </div>
                                    <div className="bg-red-50 p-3 rounded-lg">
                                      <p className="text-sm text-red-700">
                                        <strong>AI Reason:</strong> {item.reason}
                                      </p>
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Action</Label>
                                      <Select value={moderationActionType} onValueChange={setModerationActionType}>
                                        <SelectTrigger>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="approve">Approve (Keep Post)</SelectItem>
                                          <SelectItem value="remove">Remove Post</SelectItem>
                                          <SelectItem value="warn">Warn User</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Checkbox 
                                        checked={addStrikeOnAction} 
                                        onCheckedChange={setAddStrikeOnAction} 
                                      />
                                      <Label>Add strike to user account</Label>
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Reason (optional)</Label>
                                      <Textarea 
                                        value={moderationReason}
                                        onChange={(e) => setModerationReason(e.target.value)}
                                        placeholder="Add notes about this decision..."
                                      />
                                    </div>
                                    <Button 
                                      onClick={() => handleReviewContent(item.content_id, false)}
                                      className="w-full bg-teal hover:bg-teal-deep"
                                    >
                                      Confirm Action
                                    </Button>
                                  </div>
                                </DialogContent>
                              </Dialog>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Reports View */}
            {moderationView === 'reports' && (
              <div className="bg-white rounded-xl border border-border-subtle overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Reported By</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Content Author</TableHead>
                      <TableHead>Content Preview</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contentReports.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-navy/60">
                          No reports to review
                        </TableCell>
                      </TableRow>
                    ) : (
                      contentReports.map((report) => (
                        <TableRow key={report.id}>
                          <TableCell>
                            {report.reporter?.first_name} {report.reporter?.last_name}
                          </TableCell>
                          <TableCell className="capitalize">{report.content_type?.replace('_', ' ')}</TableCell>
                          <TableCell className="capitalize">{report.reason?.replace('_', ' ')}</TableCell>
                          <TableCell>
                            {report.content_author?.first_name} {report.content_author?.last_name}
                          </TableCell>
                          <TableCell className="max-w-xs truncate" title={report.content_preview}>
                            {report.content_preview?.substring(0, 50)}...
                          </TableCell>
                          <TableCell>
                            {report.status === 'reviewed' ? (
                              <Badge className="bg-green-100 text-green-700">Reviewed</Badge>
                            ) : (
                              <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {report.status !== 'reviewed' && (
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button size="sm" variant="outline">
                                    <Eye size={14} className="mr-1" /> Review
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>Review Report</DialogTitle>
                                  </DialogHeader>
                                  <div className="space-y-4">
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                      <p className="text-sm font-medium text-navy/60 mb-2">Reported Content:</p>
                                      <p className="text-sm">{report.content_preview}</p>
                                    </div>
                                    {report.details && (
                                      <div className="bg-blue-50 p-3 rounded-lg">
                                        <p className="text-sm text-blue-700">
                                          <strong>Reporter Notes:</strong> {report.details}
                                        </p>
                                      </div>
                                    )}
                                    <div className="space-y-2">
                                      <Label>Action</Label>
                                      <Select value={moderationActionType} onValueChange={setModerationActionType}>
                                        <SelectTrigger>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="approve">Dismiss Report (Keep Post)</SelectItem>
                                          <SelectItem value="remove">Remove Post</SelectItem>
                                          <SelectItem value="warn">Warn Author</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Checkbox 
                                        checked={addStrikeOnAction} 
                                        onCheckedChange={setAddStrikeOnAction} 
                                      />
                                      <Label>Add strike to content author</Label>
                                    </div>
                                    <Button 
                                      onClick={() => handleReviewContent(report.id, true)}
                                      className="w-full bg-teal hover:bg-teal-deep"
                                    >
                                      Confirm Action
                                    </Button>
                                  </div>
                                </DialogContent>
                              </Dialog>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Actions Log View */}
            {moderationView === 'actions' && (
              <div className="bg-white rounded-xl border border-border-subtle overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Admin</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Strike Added</TableHead>
                      <TableHead>Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {moderationActions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-navy/60">
                          No moderation actions recorded
                        </TableCell>
                      </TableRow>
                    ) : (
                      moderationActions.map((action) => (
                        <TableRow key={action.id}>
                          <TableCell>{new Date(action.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>{action.admin_email}</TableCell>
                          <TableCell className="capitalize">
                            <Badge className={
                              action.action === 'remove' ? 'bg-red-100 text-red-700' :
                              action.action === 'warn' ? 'bg-orange-100 text-orange-700' :
                              'bg-green-100 text-green-700'
                            }>
                              {action.action}
                            </Badge>
                          </TableCell>
                          <TableCell>{action.strike_added ? 'Yes' : 'No'}</TableCell>
                          <TableCell className="max-w-xs truncate">{action.reason || '-'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          {/* Circles Tab */}
          <TabsContent value="circles" data-testid="admin-circles">
            {circleDetailView && circleDetail ? (
              /* Circle Detail View */
              <div>
                <button onClick={() => { setCircleDetailView(null); setCircleDetail(null); }} className="flex items-center gap-2 text-teal hover:text-teal-deep mb-6">
                  <ArrowLeft size={18} /> Back to Circles
                </button>
                <div className="bg-white rounded-2xl shadow-card p-6 mb-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h2 className="font-display text-2xl text-navy">{circleDetail.name}</h2>
                      <p className="text-text-mid flex items-center gap-1 mt-1"><MapPin size={14} /> {circleDetail.city}</p>
                      {circleDetail.notes && <p className="text-sm text-text-light mt-1">{circleDetail.notes}</p>}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => setCircleEventModal({ open: true, circleId: circleDetail.id })} className="bg-teal hover:bg-teal-deep text-white rounded-full" data-testid="schedule-circle-event-btn">
                        <Plus size={14} className="mr-1" /> Schedule Event
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDissolveCircle(circleDetail.id)} className="border-red-300 text-red-600 hover:bg-red-50 rounded-full" data-testid="dissolve-circle-btn">
                        Dissolve Circle
                      </Button>
                    </div>
                  </div>
                  
                  <h3 className="font-semibold text-navy mb-3">Members ({circleDetail.members?.length || 0})</h3>
                  <div className="space-y-2 mb-6">
                    {circleDetail.members?.map(m => (
                      <div key={m.id} className="flex items-center justify-between p-3 bg-cream/50 rounded-xl">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarFallback className="bg-seafoam/40 text-teal">{m.first_name?.[0]}{m.last_name?.[0]}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-navy">{m.first_name} {m.last_name}</p>
                            <p className="text-xs text-text-mid">{m.email} &bull; {m.city}</p>
                          </div>
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => handleRemoveMemberFromCircle(circleDetail.id, m.id)} className="text-red-500 hover:bg-red-50" data-testid={`remove-circle-member-${m.id}`}>
                          <UserX size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                  
                  {/* Add member - simple dropdown from Thrive members not in circle */}
                  <div className="border-t border-border-subtle pt-4 mb-6">
                    <h4 className="text-sm font-medium text-navy mb-2">Add Member</h4>
                    <div className="flex gap-2">
                      <Select onValueChange={(uid) => handleAddMemberToCircle(circleDetail.id, uid)}>
                        <SelectTrigger className="rounded-xl" data-testid="add-circle-member-select">
                          <SelectValue placeholder="Select a Thrive member..." />
                        </SelectTrigger>
                        <SelectContent>
                          {members.filter(m => (m.membership_tier === 'premium' || m.membership_tier === 'thrive') && !circleDetail.members?.find(cm => cm.id === m.id)).map(m => (
                            <SelectItem key={m.id} value={m.id}>{m.first_name} {m.last_name} - {m.city}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <h3 className="font-semibold text-navy mb-3">Events</h3>
                  {circleDetail.events?.length > 0 ? (
                    <div className="space-y-2">
                      {circleDetail.events.map(ev => (
                        <div key={ev.id} className={`flex items-center justify-between p-3 rounded-xl ${ev.is_cancelled ? 'bg-red-50 opacity-60' : 'bg-cream/50'}`}>
                          <div>
                            <p className="font-medium text-navy">{ev.title} {ev.is_cancelled && <Badge variant="outline" className="ml-2 text-red-600 border-red-200">Cancelled</Badge>}</p>
                            <p className="text-xs text-text-mid">{new Date(ev.event_date).toLocaleString()} &bull; {ev.event_type === 'zoom' ? 'Zoom' : 'In-Person'} &bull; {ev.rsvp_count || 0} RSVPs</p>
                          </div>
                          {!ev.is_cancelled && (
                            <Button size="sm" variant="ghost" onClick={() => handleCancelCircleEvent(ev.id)} className="text-red-500 hover:bg-red-50">Cancel</Button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-text-light">No events scheduled</p>
                  )}
                </div>
              </div>
            ) : (
              /* Circles List */
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-display text-2xl text-navy">Circles</h2>
                  <Dialog open={createCircleOpen} onOpenChange={setCreateCircleOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-teal hover:bg-teal-deep rounded-full" data-testid="create-circle-btn">
                        <Plus size={18} className="mr-2" /> Create Circle
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle className="font-display text-navy">Create New Circle</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Circle Name *</Label>
                            <Input value={newCircleName} onChange={(e) => setNewCircleName(e.target.value)} placeholder="e.g., West LA Circle" className="rounded-xl" data-testid="circle-name-input" />
                          </div>
                          <div>
                            <Label>Home City</Label>
                            <Input value={newCircleCity} onChange={(e) => setNewCircleCity(e.target.value)} placeholder="e.g., Los Angeles" className="rounded-xl" data-testid="circle-city-input" />
                          </div>
                        </div>
                        <div>
                          <Label>Notes (optional)</Label>
                          <Textarea value={newCircleNotes} onChange={(e) => setNewCircleNotes(e.target.value)} placeholder="Circle theme or vibe..." rows={2} className="rounded-xl" data-testid="circle-notes-input" />
                        </div>
                        <div>
                          <Label>Select Members (2-8)</Label>
                          <p className="text-sm text-text-mid mb-2">Selected: {selectedMembers.length}</p>
                          <div className="max-h-60 overflow-y-auto border rounded-xl p-2 space-y-2">
                            {members.filter(m => m.membership_tier === 'premium' || m.membership_tier === 'thrive').map(member => (
                              <label key={member.id} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer ${selectedMembers.includes(member.id) ? 'bg-teal/10' : 'hover:bg-cream'}`}>
                                <Checkbox
                                  checked={selectedMembers.includes(member.id)}
                                  onCheckedChange={(checked) => {
                                    if (checked) setSelectedMembers([...selectedMembers, member.id]);
                                    else setSelectedMembers(selectedMembers.filter(id => id !== member.id));
                                  }}
                                />
                                <span>{member.first_name} {member.last_name} - {member.city}</span>
                              </label>
                            ))}
                            {members.filter(m => m.membership_tier === 'premium' || m.membership_tier === 'thrive').length === 0 && (
                              <p className="text-sm text-text-light p-2">No Thrive members available. All members can be selected.</p>
                            )}
                            {members.filter(m => m.membership_tier !== 'premium' && m.membership_tier !== 'thrive').length > 0 &&
                             members.filter(m => m.membership_tier === 'premium' || m.membership_tier === 'thrive').length === 0 &&
                              members.map(member => (
                                <label key={member.id} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer ${selectedMembers.includes(member.id) ? 'bg-teal/10' : 'hover:bg-cream'}`}>
                                  <Checkbox checked={selectedMembers.includes(member.id)} onCheckedChange={(checked) => {
                                    if (checked) setSelectedMembers([...selectedMembers, member.id]);
                                    else setSelectedMembers(selectedMembers.filter(id => id !== member.id));
                                  }} />
                                  <span>{member.first_name} {member.last_name} - {member.city}</span>
                                </label>
                              ))
                            }
                          </div>
                        </div>
                        <Button onClick={handleCreateCircle} disabled={selectedMembers.length < 2 || selectedMembers.length > 8 || !newCircleName} className="w-full bg-teal hover:bg-teal-deep rounded-full" data-testid="submit-create-circle-btn">
                          Create Circle
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>

                {circles.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-2xl shadow-card">
                    <Users className="mx-auto text-text-light mb-4" size={40} />
                    <p className="text-text-mid">No circles yet. Create one to get started!</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto bg-white rounded-2xl shadow-card">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Circle Name</TableHead>
                          <TableHead>City</TableHead>
                          <TableHead>Members</TableHead>
                          <TableHead>Created</TableHead>
                          <TableHead>Next Event</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {circles.map(circle => (
                          <TableRow key={circle.id} className="cursor-pointer hover:bg-cream/50" data-testid={`circle-row-${circle.id}`}>
                            <TableCell className="font-medium text-navy" onClick={() => openCircleDetail(circle.id)}>{circle.name}</TableCell>
                            <TableCell>{circle.city}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="flex -space-x-1">
                                  {circle.members?.slice(0, 4).map(m => (
                                    <Avatar key={m.id} className="w-6 h-6 border border-white">
                                      <AvatarFallback className="bg-seafoam/40 text-teal text-[10px]">{m.first_name?.[0]}</AvatarFallback>
                                    </Avatar>
                                  ))}
                                </div>
                                <span className="text-sm">{circle.member_count}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">{new Date(circle.created_at).toLocaleDateString()}</TableCell>
                            <TableCell>
                              {circle.next_event ? (
                                <div>
                                  <p className="text-sm font-medium">{circle.next_event.title}</p>
                                  <p className="text-xs text-text-light">{new Date(circle.next_event.event_date).toLocaleDateString()}</p>
                                </div>
                              ) : <span className="text-sm text-text-light">None</span>}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex gap-1 justify-end">
                                <Button size="sm" variant="ghost" onClick={() => openCircleDetail(circle.id)} className="text-teal" data-testid={`view-circle-${circle.id}`}>View</Button>
                                <Button size="sm" variant="ghost" onClick={() => setCircleEventModal({ open: true, circleId: circle.id })} className="text-teal">+ Event</Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Circle Requests Tab */}
          <TabsContent value="circle-requests" data-testid="admin-circle-requests">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-2xl text-navy">Circle Requests</h2>
              <Badge className="bg-teal/10 text-teal">{circleRequests.filter(r => r.status === 'pending').length} pending</Badge>
            </div>
            {circleRequests.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl shadow-card">
                <Crown className="mx-auto text-text-light mb-4" size={40} />
                <p className="text-text-mid">No circle requests yet</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-card overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Member</TableHead>
                      <TableHead>City / Zip</TableHead>
                      <TableHead>Preferred Days</TableHead>
                      <TableHead>Format</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Requested</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {circleRequests.map(req => (
                      <TableRow key={req.id} data-testid={`circle-request-${req.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-navy">{req.user_name || req.user?.first_name}</p>
                            <p className="text-xs text-text-mid">{req.user_email || req.user?.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">{req.city}</p>
                          <Badge variant="outline" className="text-xs font-mono">{req.zip_code}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {req.preferred_days?.map(d => (
                              <span key={d} className="text-xs bg-seafoam/40 px-2 py-0.5 rounded-full">{d}</span>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm capitalize">{req.preferred_format?.replace('_', ' ')}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            req.status === 'pending' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                            req.status === 'waitlisted' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                            req.status === 'assigned' ? 'bg-green-50 text-green-700 border-green-200' :
                            ''
                          }>
                            {req.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{new Date(req.created_at).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right">
                          {req.status === 'pending' || req.status === 'waitlisted' ? (
                            <div className="flex gap-1 justify-end">
                              {circles.length > 0 && (
                                <Select onValueChange={(cid) => handleAssignRequest(req.id, cid)}>
                                  <SelectTrigger className="w-[140px] h-8 text-xs rounded-lg" data-testid={`assign-request-${req.id}`}>
                                    <SelectValue placeholder="Assign to..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {circles.map(c => (
                                      <SelectItem key={c.id} value={c.id}>{c.name} ({c.member_count})</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                              {req.status !== 'waitlisted' && (
                                <Button size="sm" variant="ghost" onClick={() => handleWaitlistRequest(req.id)} className="text-amber-600 text-xs">Waitlist</Button>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-text-light">Assigned</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events" data-testid="admin-events">
            <AdminEventsTab
              selectedEvent={selectedEvent}
              eventDetail={eventDetail}
              setSelectedEvent={setSelectedEvent}
              setEventDetail={setEventDetail}
              events={events}
              members={members}
              cities={CITIES}
              addRsvpOpen={addRsvpOpen}
              setAddRsvpOpen={setAddRsvpOpen}
              addRsvpUserId={addRsvpUserId}
              setAddRsvpUserId={setAddRsvpUserId}
              setGuestModal={setGuestModal}
              createEventOpen={createEventOpen}
              setCreateEventOpen={setCreateEventOpen}
              newEvent={newEvent}
              setNewEvent={setNewEvent}
              setEditingEvent={setEditingEvent}
              setEditEventOpen={setEditEventOpen}
              handleExportEventRsvps={handleExportEventRsvps}
              handleDeleteEvent={handleDeleteEvent}
              handleAddRsvp={handleAddRsvp}
              handleRemoveRsvp={handleRemoveRsvp}
              handlePromoteFromWaitlist={handlePromoteFromWaitlist}
              handleRefundPayment={handleRefundPayment}
              handleTransferCredit={handleTransferCredit}
              handleCreateEvent={handleCreateEvent}
              fetchEventDetail={fetchEventDetail}
            />
          </TabsContent>

          {/* Waitlist Tab */}
          {/* Waitlist Tab */}
          <TabsContent value="waitlist" data-testid="admin-waitlist">
            {/* Header with stats and actions */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
              <div>
                <h2 className="font-display text-2xl text-navy">Waitlist</h2>
                <p className="text-text-mid text-sm mt-1">
                  {waitlistTotal} people waiting to join
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button 
                  variant="outline" 
                  onClick={handleExportWaitlist}
                  className="rounded-full border-teal text-teal hover:bg-teal hover:text-white"
                  data-testid="export-waitlist-btn"
                >
                  <Download size={16} className="mr-2" /> Export CSV
                </Button>
              </div>
            </div>

            {/* City Stats Cards */}
            {waitlistCityStats.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
                {waitlistCityStats.slice(0, 6).map(stat => (
                  <div 
                    key={stat.city} 
                    className="bg-white rounded-xl p-4 shadow-card cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => setWaitlistCityFilter(stat.city)}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin size={14} className="text-teal" />
                      <span className="text-xs text-text-mid truncate">{stat.city}</span>
                    </div>
                    <p className="text-2xl font-bold text-navy">{stat.count}</p>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBulkInviteCity(stat.city);
                      }}
                      className="w-full mt-2 text-xs text-teal hover:bg-teal/10"
                      data-testid={`bulk-invite-${stat.city}`}
                    >
                      <Send size={12} className="mr-1" /> Invite All
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Filter */}
            <div className="bg-white rounded-2xl shadow-card p-4 mb-6">
              <div className="flex flex-wrap gap-4 items-center">
                <Select value={waitlistCityFilter} onValueChange={setWaitlistCityFilter}>
                  <SelectTrigger className="w-[200px] rounded-xl">
                    <SelectValue placeholder="Filter by city" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Cities ({waitlistTotal})</SelectItem>
                    {waitlistCityStats.map(stat => (
                      <SelectItem key={stat.city} value={stat.city}>
                        {stat.city} ({stat.count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {waitlistCityFilter !== 'all' && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setWaitlistCityFilter('all')}
                    className="rounded-full"
                  >
                    Clear Filter
                  </Button>
                )}
                <span className="text-sm text-text-mid ml-auto">
                  Showing {filteredWaitlist.length} of {waitlistTotal}
                </span>
              </div>
            </div>

            {/* Waitlist Table */}
            <div className="bg-white rounded-2xl shadow-card overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Zip Code</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredWaitlist.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-text-mid">
                        No waitlist signups yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredWaitlist.map(member => (
                      <TableRow key={member.id}>
                        <TableCell className="font-medium">{member.first_name}</TableCell>
                        <TableCell>{member.email}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-cream">
                            {member.city}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-mono text-xs">
                            {member.zip_code || '-'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-text-mid">
                          {new Date(member.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {member.invited ? (
                            <Badge className="bg-green-100 text-green-800">
                              Invited {member.invited_at ? new Date(member.invited_at).toLocaleDateString() : ''}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                              Waiting
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {!member.invited && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleInviteWaitlistMember(member.id, member.email)}
                                className="rounded-full border-teal text-teal hover:bg-teal hover:text-white"
                                data-testid={`invite-waitlist-${member.id}`}
                              >
                                <Mail size={14} className="mr-1" /> Invite
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteWaitlistMember(member.id, member.email)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-waitlist-${member.id}`}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Revenue Tab */}
          <TabsContent value="revenue" data-testid="admin-revenue">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Monthly Revenue Chart Placeholder */}
              <div className="bg-white rounded-2xl p-6 shadow-card">
                <h3 className="font-semibold text-navy mb-4">Monthly Revenue (MRR)</h3>
                <div className="space-y-3">
                  {revenueData?.monthly_revenue?.slice(-6).map((item, i) => (
                    <div key={item.month} className="flex items-center gap-4">
                      <span className="text-sm text-text-mid w-20">{item.month}</span>
                      <div className="flex-1 bg-cream rounded-full h-6 overflow-hidden">
                        <div 
                          className="bg-teal h-full rounded-full"
                          style={{ width: `${Math.min((item.revenue / 10000) * 100, 100)}%` }}
                        />
                      </div>
                      <span className="text-sm font-semibold text-navy w-20 text-right">
                        ${item.revenue.toFixed(0)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Webhook Logs */}
              <div className="bg-white rounded-2xl p-6 shadow-card">
                <h3 className="font-semibold text-navy mb-4">Recent Webhook Events</h3>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {revenueData?.webhook_logs?.map(log => (
                    <div key={log.id} className="flex items-center justify-between p-3 bg-cream/50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-navy">{log.event_type}</p>
                        <p className="text-xs text-text-light">{new Date(log.created_at).toLocaleString()}</p>
                      </div>
                      <Badge variant="outline" className="text-xs">{log.event_id?.slice(0, 8)}</Badge>
                    </div>
                  ))}
                  {(!revenueData?.webhook_logs || revenueData.webhook_logs.length === 0) && (
                    <p className="text-text-mid text-sm text-center py-4">No webhook events yet</p>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ===== PROMO CODES TAB ===== */}
          <TabsContent value="promo-codes" data-testid="admin-promo-codes">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="font-display text-2xl text-navy">Promo Codes</h2>
                <p className="text-text-mid text-sm mt-1">Create and manage promotional access codes</p>
              </div>
              <Button
                onClick={() => { setEditingPromoId(null); setEditingPromoUsage(0); setNewPromo({ code: '', discount_type: 'free_trial', tier: 'thrive', duration_days: '10', percent_off: 10, applicable_plans: [], usage_limit: '50', expiry_date: '', label: '' }); setCreatePromoOpen(true); }}
                className="bg-teal hover:bg-teal-deep text-white rounded-full"
                data-testid="create-promo-btn"
              >
                <Plus size={16} className="mr-1" /> Create Promo Code
              </Button>
            </div>

            {/* Active Promo Codes Table */}
            <div className="bg-white rounded-2xl shadow-card p-6 mb-6">
              <h3 className="font-semibold text-navy mb-4">All Promo Codes</h3>
              {promoLoading ? (
                <div className="text-center py-8"><Loader2 className="animate-spin mx-auto text-teal" /></div>
              ) : promoCodes.length === 0 ? (
                <p className="text-text-mid text-sm text-center py-6">No promo codes yet. Create your first one!</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead>
                        <TableHead>Label</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Details</TableHead>
                        <TableHead>Usage</TableHead>
                        <TableHead>Expiry</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {promoCodes.map(pc => {
                        const isPercent = pc.discount_type === 'percent_off';
                        return (
                        <TableRow key={pc.id} data-testid={`promo-row-${pc.code}`}>
                          <TableCell className="font-mono font-bold text-navy">{pc.code}</TableCell>
                          <TableCell className="text-text-mid text-sm">{pc.label || '—'}</TableCell>
                          <TableCell>
                            {isPercent ? (
                              <Badge className="bg-amber-100 text-amber-800">{pc.percent_off}% off</Badge>
                            ) : (
                              <Badge className="bg-purple-100 text-purple-700">Free Trial</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-xs">
                            {isPercent ? (
                              <span className="text-text-mid">
                                {(pc.applicable_plans || []).map(p => p.replace('_', ' ')).join(', ') || '—'}
                              </span>
                            ) : (
                              <span>{pc.tier} · {pc.duration_days}d</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {pc.usage_count} / {pc.usage_limit ?? '∞'}
                          </TableCell>
                          <TableCell className="text-sm">
                            {pc.expiry_date ? new Date(pc.expiry_date).toLocaleDateString() : 'No expiry'}
                          </TableCell>
                          <TableCell>
                            <Badge className={pc.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                              {pc.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                data-testid={`edit-promo-${pc.code}`}
                                onClick={() => {
                                  setEditingPromoId(pc.id);
                                  setEditingPromoUsage(pc.usage_count || 0);
                                  setNewPromo({
                                    code: pc.code || '',
                                    discount_type: pc.discount_type || 'free_trial',
                                    tier: pc.tier || 'thrive',
                                    duration_days: String(pc.duration_days ?? '10'),
                                    percent_off: pc.percent_off ?? 10,
                                    applicable_plans: pc.applicable_plans || [],
                                    usage_limit: pc.usage_limit == null ? '' : String(pc.usage_limit),
                                    expiry_date: pc.expiry_date ? pc.expiry_date.slice(0, 10) : '',
                                    label: pc.label || '',
                                  });
                                  setCreatePromoOpen(true);
                                }}
                                className="text-navy hover:bg-cream/50"
                              >
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                data-testid={`toggle-promo-${pc.code}`}
                                onClick={async () => {
                                  try {
                                    await api.patch(`/promo/admin/codes/${pc.id}`, { is_active: !pc.is_active }, { headers: getAuthHeader() });
                                    toast.success(pc.is_active ? 'Promo deactivated' : 'Promo activated');
                                    fetchData();
                                  } catch { toast.error('Failed to update promo'); }
                                }}
                                className={pc.is_active ? 'text-red-500 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}
                              >
                                {pc.is_active ? 'Deactivate' : 'Activate'}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>

            {/* Promo Members Table */}
            <div className="bg-white rounded-2xl shadow-card p-6">
              <h3 className="font-semibold text-navy mb-4">
                Promo Members ({promoMembers.filter(m => m.promo_status === 'active').length} active)
              </h3>
              {promoMembers.length === 0 ? (
                <p className="text-text-mid text-sm text-center py-6">No members using promo codes yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Promo Code</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Days Remaining</TableHead>
                        <TableHead>Expiry</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {promoMembers.map(m => {
                        const endDate = m.promo_end ? new Date(m.promo_end) : null;
                        const daysLeft = endDate ? Math.max(0, Math.ceil((endDate - new Date()) / (1000*60*60*24))) : 0;
                        return (
                          <TableRow key={m.id} data-testid={`promo-member-${m.id}`}>
                            <TableCell className="font-medium text-navy">{m.first_name} {m.last_name}</TableCell>
                            <TableCell className="text-sm text-text-mid">{m.email}</TableCell>
                            <TableCell className="font-mono text-sm">{m.promo_code}</TableCell>
                            <TableCell>
                              <Badge className={m.promo_status === 'active' ? 'bg-green-100 text-green-700' : m.promo_status === 'upgraded' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}>
                                {m.promo_status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {m.promo_status === 'active' ? (
                                <span className={daysLeft <= 2 ? 'text-red-600 font-semibold' : 'text-navy'}>{daysLeft} days</span>
                              ) : '—'}
                            </TableCell>
                            <TableCell className="text-sm">{endDate ? endDate.toLocaleDateString() : '—'}</TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {m.promo_status === 'active' && (
                                  <Button
                                    variant="ghost" size="sm"
                                    className="text-teal hover:bg-teal/10 text-xs"
                                    data-testid={`extend-promo-${m.id}`}
                                    onClick={() => { setExtendPromoModal({ open: true, member: m }); setExtendDays('10'); }}
                                  >
                                    Extend
                                  </Button>
                                )}
                                {(m.promo_status === 'active' || m.promo_status === 'expired') && (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="sm" className="text-xs" data-testid={`upgrade-promo-${m.id}`}>
                                        Upgrade
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent>
                                      {['connect', 'thrive'].map(tier => (
                                        <DropdownMenuItem key={tier} onClick={async () => {
                                          try {
                                            await api.patch(`/promo/admin/members/${m.id}/upgrade`, { tier }, { headers: getAuthHeader() });
                                            toast.success(`Upgraded to ${tier}`);
                                            fetchData();
                                          } catch { toast.error('Upgrade failed'); }
                                        }}>
                                          Upgrade to {tier.charAt(0).toUpperCase() + tier.slice(1)}
                                        </DropdownMenuItem>
                                      ))}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ===== MARKETING TAB ===== */}
          <TabsContent value="marketing" data-testid="admin-marketing">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="font-display text-2xl text-navy">Marketing QR Codes</h2>
                <p className="text-text-mid text-sm mt-1">Create trackable QR codes for print marketing — restaurants, gyms, studios, etc.</p>
              </div>
              <Button
                onClick={() => { setNewQr({ label: '', location: '', ref_code: '' }); setCreateQrOpen(true); }}
                className="bg-teal hover:bg-teal-deep text-white rounded-full"
                data-testid="create-qr-btn"
              >
                <Plus size={16} className="mr-1" /> Create QR Code
              </Button>
            </div>

            {qrLoading ? (
              <div className="text-center py-12"><Loader2 className="animate-spin mx-auto text-teal" /></div>
            ) : qrCodes.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-card p-12 text-center">
                <Tag className="mx-auto text-text-light mb-4" size={40} />
                <p className="text-text-mid mb-2">No QR codes yet</p>
                <p className="text-text-light text-sm">Create your first QR code to start tracking signups from print marketing.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {qrCodes.map(qr => {
                  const siteUrl = 'https://calimomsconnect.com';
                  const qrUrl = `${siteUrl}?ref=${qr.ref_code}`;
                  return (
                    <div key={qr.id} className="bg-white rounded-2xl shadow-card p-6" data-testid={`qr-card-${qr.ref_code}`}>
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-semibold text-navy">{qr.label}</h3>
                          {qr.location && <p className="text-text-mid text-sm flex items-center gap-1 mt-0.5"><MapPin size={12} /> {qr.location}</p>}
                        </div>
                        <Badge className={qr.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                          {qr.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>

                      <div className="flex justify-center mb-4 bg-white p-3 rounded-xl border border-border-subtle">
                        <QRCodeCanvas
                          value={qrUrl}
                          size={180}
                          level="H"
                          includeMargin={true}
                          id={`qr-canvas-${qr.id}`}
                        />
                      </div>

                      <p className="text-xs text-text-light text-center mb-4 font-mono break-all">{qrUrl}</p>

                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="bg-seafoam/20 rounded-xl p-3 text-center">
                          <p className="text-2xl font-bold text-navy">{qr.scans}</p>
                          <p className="text-xs text-text-mid">Scans</p>
                        </div>
                        <div className="bg-teal/10 rounded-xl p-3 text-center">
                          <p className="text-2xl font-bold text-teal">{qr.signups}</p>
                          <p className="text-xs text-text-mid">Signups</p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 rounded-full text-xs"
                          data-testid={`download-qr-${qr.ref_code}`}
                          onClick={() => {
                            const canvas = document.getElementById(`qr-canvas-${qr.id}`);
                            if (canvas) {
                              const url = canvas.toDataURL('image/png');
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `CMC_QR_${qr.ref_code}.png`;
                              a.click();
                            }
                          }}
                        >
                          Download PNG
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full text-xs"
                          data-testid={`view-signups-${qr.ref_code}`}
                          onClick={async () => {
                            try {
                              const res = await api.get(`/admin/qr-codes/${qr.id}/signups`, { headers: getAuthHeader() });
                              setQrSignupsModal({ open: true, qr: res.data.qr, members: res.data.members });
                            } catch { toast.error('Failed to load signups'); }
                          }}
                        >
                          View Signups
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full text-xs px-2"
                          data-testid={`delete-qr-${qr.ref_code}`}
                          onClick={async () => {
                            if (!window.confirm('Delete this QR code?')) return;
                            try {
                              await api.delete(`/admin/qr-codes/${qr.id}`, { headers: getAuthHeader() });
                              toast.success('QR code deleted');
                              fetchData();
                            } catch { toast.error('Failed to delete'); }
                          }}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Testimonials Tab — main admin only */}
          <TabsContent value="testimonials" data-testid="admin-testimonials">
            <AdminTestimonialsTab
              getAuthHeader={getAuthHeader}
              isMainAdmin={authUser?.is_admin === true}
            />
          </TabsContent>

          {/* Referrals Tab — main admin only */}
          <TabsContent value="referrals" data-testid="admin-referrals">
            <AdminReferralsTab getAuthHeader={getAuthHeader} />
          </TabsContent>

          {/* Reports Tab — main admin only */}
          <TabsContent value="reports" data-testid="admin-reports">
            <AdminReportsTab getAuthHeader={getAuthHeader} />
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" data-testid="admin-analytics">
            <AdminAnalyticsTab
              analytics={analytics}
              analyticsLoading={analyticsLoading}
              fetchAnalytics={fetchAnalytics}
            />
          </TabsContent>

          {/* Metrics Tab */}
          <TabsContent value="metrics" data-testid="admin-metrics">
            <AdminMetricsTab />
          </TabsContent>

          {/* Help Requests Tab */}
          <TabsContent value="help-requests" data-testid="admin-help-requests">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="font-display text-2xl text-navy">Help Requests</h2>
                <p className="text-text-mid text-sm mt-1">Support messages submitted by members.</p>
              </div>
              <Badge variant="outline" className="text-xs" data-testid="help-requests-count">
                {helpRequests.length} total
              </Badge>
            </div>
            {helpRequests.length === 0 ? (
              <div className="bg-white rounded-2xl p-10 shadow-card text-center text-text-mid">
                No help requests yet.
              </div>
            ) : (
              <div className="space-y-3">
                {helpRequests.map((r) => (
                  <div
                    key={r.id}
                    className="bg-white rounded-2xl p-5 shadow-card"
                    data-testid={`help-request-${r.id}`}
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-[240px]">
                        <div className="flex items-center gap-2 flex-wrap">
                          {r.admin_unread && (
                            <span
                              className="inline-block w-2.5 h-2.5 rounded-full bg-red-500 flex-shrink-0"
                              data-testid={`help-unread-dot-${r.id}`}
                              title="Unread — member replied"
                            />
                          )}
                          <span className="font-semibold text-navy">{r.subject || 'Help Request'}</span>
                          <Badge
                            className={
                              r.status === 'resolved'
                                ? 'bg-green-100 text-green-700 text-[10px]'
                                : r.status === 'in_progress'
                                ? 'bg-yellow-100 text-yellow-700 text-[10px]'
                                : 'bg-red-100 text-red-700 text-[10px]'
                            }
                          >
                            {(r.status || 'open').replace('_', ' ')}
                          </Badge>
                        </div>
                        <p className="text-xs text-text-mid mt-1">
                          From <span className="font-medium text-navy">{r.user_name}</span> ({r.user_email}) •{' '}
                          {r.created_at ? new Date(r.created_at).toLocaleString() : ''}
                        </p>
                        <p className="text-sm mt-2 text-navy/90 whitespace-pre-wrap">{r.message}</p>
                        {Array.isArray(r.replies) && r.replies.length > 1 && (
                          <p className="text-[11px] text-text-mid mt-2">
                            <span className="font-semibold">{r.replies.length}</span> messages in this thread
                            {r.last_reply_at && <> · last on {new Date(r.last_reply_at).toLocaleString()}</>}
                          </p>
                        )}
                        {r.last_reply && (
                          <div className="mt-3 bg-teal/5 border-l-4 border-teal rounded-r-lg p-3">
                            <p className="text-xs font-semibold text-teal">Last admin reply ({r.last_reply_by || 'admin'})</p>
                            <p className="text-sm mt-1 whitespace-pre-wrap line-clamp-3">{r.last_reply}</p>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          className="bg-teal hover:bg-teal-deep text-white rounded-full text-xs"
                          onClick={() => openHelpReplyModal(r)}
                          data-testid={`help-reply-btn-${r.id}`}
                        >
                          <Send size={12} className="mr-1" /> View / Reply
                        </Button>
                        <Select
                          value={r.status || 'open'}
                          onValueChange={(val) => handleUpdateHelpStatus(r.id, val)}
                        >
                          <SelectTrigger
                            className="w-[130px] h-8 text-xs rounded-full"
                            data-testid={`help-status-${r.id}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="resolved">Resolved</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>




          {/* Team Management Tab (Co-Admins) */}
          {isSuperAdmin && (
            <TabsContent value="team-management" data-testid="team-management-tab">
              <TeamManagementTab getAuthHeader={getAuthHeader} />
            </TabsContent>
          )}

          {/* Admin Management Tab (Super Admin Only) */}
          {isSuperAdmin && (
            <TabsContent value="admin-management" data-testid="admin-management-tab">
              <AdminManagementTab
                adminsList={adminsList}
                inviteAdminOpen={inviteAdminOpen}
                setInviteAdminOpen={setInviteAdminOpen}
                newAdmin={newAdmin}
                setNewAdmin={setNewAdmin}
                inviting={inviting}
                handleInviteAdmin={handleInviteAdmin}
                handleUnlockAdmin={handleUnlockAdmin}
                handleRevokeAdmin={handleRevokeAdmin}
                handleForceDisableMfa={handleForceDisableMfa}
                mfaStatus={mfaStatus}
                fetchMfaStatus={fetchMfaStatus}
                mfaBackupCodes={mfaBackupCodes}
                setMfaBackupCodes={setMfaBackupCodes}
                mfaSetupData={mfaSetupData}
                setMfaSetupData={setMfaSetupData}
                mfaVerifyCode={mfaVerifyCode}
                setMfaVerifyCode={setMfaVerifyCode}
                mfaDisablePassword={mfaDisablePassword}
                setMfaDisablePassword={setMfaDisablePassword}
                mfaLoading={mfaLoading}
                handleMfaSetup={handleMfaSetup}
                handleMfaVerify={handleMfaVerify}
                handleMfaDisable={handleMfaDisable}
              />
            </TabsContent>
          )}

          {/* Audit Log Tab (Super Admin Only) */}
          {isSuperAdmin && (
            <TabsContent value="audit-log" data-testid="audit-log-tab">
              <AdminAuditLogTab
                auditFilters={auditFilters}
                setAuditFilters={setAuditFilters}
                actionTypes={actionTypes}
                adminsList={adminsList}
                auditLogs={auditLogs}
                fetchData={fetchData}
              />
            </TabsContent>
          )}
        </Tabs>
      </main>

      {/* ===== CIRCLE EVENT MODAL ===== */}
      <Dialog open={circleEventModal.open} onOpenChange={(open) => !open && setCircleEventModal({ open: false, circleId: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">Schedule Circle Event</DialogTitle>
            <DialogDescription>Choose Zoom or In-Person. All circle members will be notified.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[50vh] overflow-y-auto">
            <div>
              <Label>Event Title *</Label>
              <Input value={circleEventForm.title} onChange={(e) => setCircleEventForm(p => ({...p, title: e.target.value}))} placeholder="e.g., Welcome Zoom Call" className="rounded-xl" data-testid="circle-event-title" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={circleEventForm.event_type} onValueChange={(v) => setCircleEventForm(p => ({...p, event_type: v}))}>
                  <SelectTrigger className="rounded-xl" data-testid="circle-event-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="zoom">Zoom Meeting</SelectItem>
                    <SelectItem value="in_person">In-Person Event</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Duration</Label>
                <Select value={circleEventForm.duration} onValueChange={(v) => setCircleEventForm(p => ({...p, duration: v}))}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30 minutes">30 min</SelectItem>
                    <SelectItem value="45 minutes">45 min</SelectItem>
                    <SelectItem value="60 minutes">1 hour</SelectItem>
                    <SelectItem value="90 minutes">1.5 hours</SelectItem>
                    <SelectItem value="120 minutes">2 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Date & Time *</Label>
              <Input type="datetime-local" value={circleEventForm.event_date} onChange={(e) => setCircleEventForm(p => ({...p, event_date: e.target.value}))} className="rounded-xl" data-testid="circle-event-date" />
            </div>
            {circleEventForm.event_type === 'zoom' ? (
              <div>
                <Label>Zoom Link</Label>
                <Input value={circleEventForm.zoom_link} onChange={(e) => setCircleEventForm(p => ({...p, zoom_link: e.target.value}))} placeholder="https://zoom.us/j/..." className="rounded-xl" data-testid="circle-event-zoom" />
              </div>
            ) : (
              <>
                <div>
                  <Label>Location Name</Label>
                  <Input value={circleEventForm.location_name} onChange={(e) => setCircleEventForm(p => ({...p, location_name: e.target.value}))} placeholder="e.g., Blue Bottle Coffee" className="rounded-xl" />
                </div>
                <div>
                  <Label>Address</Label>
                  <Input value={circleEventForm.address} onChange={(e) => setCircleEventForm(p => ({...p, address: e.target.value}))} placeholder="Full address" className="rounded-xl" />
                </div>
                <div>
                  <Label>Max Capacity</Label>
                  <Input type="number" value={circleEventForm.max_capacity} onChange={(e) => setCircleEventForm(p => ({...p, max_capacity: parseInt(e.target.value) || ''}))} placeholder="e.g., 10" className="rounded-xl" />
                </div>
              </>
            )}
            <div>
              <Label>Description (optional)</Label>
              <Textarea value={circleEventForm.description} onChange={(e) => setCircleEventForm(p => ({...p, description: e.target.value}))} placeholder="What's this gathering about?" rows={2} className="rounded-xl" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCircleEventModal({ open: false, circleId: null })}>Cancel</Button>
            <Button onClick={handleCreateCircleEvent} disabled={creatingCircleEvent || !circleEventForm.title || !circleEventForm.event_date} className="bg-teal hover:bg-teal-deep text-white rounded-full" data-testid="submit-circle-event-btn">
              {creatingCircleEvent ? 'Creating...' : 'Create Event'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== ACCOUNT ACTION MODAL ===== */}
      <Dialog 
        open={accountActionModal.open} 
        onOpenChange={(open) => !open && setAccountActionModal({ open: false, action: '', member: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {accountActionModal.action === 'suspend' && <><Clock size={20} className="text-amber-500" /> Suspend Account</>}
              {accountActionModal.action === 'disable' && <><Ban size={20} className="text-red-500" /> Disable Account</>}
              {accountActionModal.action === 'enable' && <><CheckCircle size={20} className="text-green-500" /> Re-enable Account</>}
              {accountActionModal.action === 'warn' && <><AlertTriangle size={20} className="text-amber-500" /> Send Warning</>}
            </DialogTitle>
            <DialogDescription>
              {accountActionModal.member && (
                <span>For: <strong>{accountActionModal.member.first_name} {accountActionModal.member.last_name}</strong> ({accountActionModal.member.email})</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {accountActionModal.action === 'suspend' && (
              <div className="space-y-2">
                <Label>Suspension Duration</Label>
                <Select value={suspendDays} onValueChange={setSuspendDays}>
                  <SelectTrigger className="rounded-xl" data-testid="suspend-days-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="14">14 days</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="60">60 days</SelectItem>
                    <SelectItem value="90">90 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder={
                  accountActionModal.action === 'suspend' ? 'Reason for suspension...' :
                  accountActionModal.action === 'disable' ? 'Reason for disabling account...' :
                  accountActionModal.action === 'enable' ? 'Reason for re-enabling...' :
                  'Warning message to send...'
                }
                rows={3}
                className="rounded-xl"
                data-testid="account-action-reason"
              />
            </div>
            {accountActionModal.action === 'disable' && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
                This will permanently disable the member's account until manually re-enabled by an admin. The member will not be able to log in.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAccountActionModal({ open: false, action: '', member: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleAccountAction}
              disabled={accountActionLoading}
              className={
                accountActionModal.action === 'disable' ? 'bg-red-600 hover:bg-red-700 text-white' :
                accountActionModal.action === 'suspend' ? 'bg-amber-600 hover:bg-amber-700 text-white' :
                accountActionModal.action === 'enable' ? 'bg-green-600 hover:bg-green-700 text-white' :
                'bg-teal hover:bg-teal-deep text-white'
              }
              data-testid="confirm-account-action-btn"
            >
              {accountActionLoading ? <Loader2 size={16} className="animate-spin mr-1" /> : null}
              {accountActionModal.action === 'suspend' ? 'Suspend Account' :
               accountActionModal.action === 'disable' ? 'Disable Account' :
               accountActionModal.action === 'enable' ? 'Re-enable Account' :
               'Send Warning'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== STATUS HISTORY MODAL ===== */}
      <Dialog 
        open={statusHistoryModal.open} 
        onOpenChange={(open) => !open && setStatusHistoryModal({ open: false, member: null })}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History size={20} className="text-teal" />
              Account Status History
            </DialogTitle>
            <DialogDescription>
              {statusHistoryModal.member && (
                <span>{statusHistoryModal.member.first_name} {statusHistoryModal.member.last_name} ({statusHistoryModal.member.email})</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto space-y-3">
            {statusHistory.length === 0 ? (
              <p className="text-center text-text-light py-4">No status changes recorded</p>
            ) : (
              statusHistory.map((entry, idx) => (
                <div key={idx} className="border border-border-subtle rounded-xl p-3">
                  <div className="flex items-center justify-between mb-1">
                    <Badge variant="outline" className={
                      entry.action === 'suspended' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                      entry.action === 'disabled' ? 'bg-red-50 text-red-700 border-red-200' :
                      entry.action === 'enabled' || entry.action === 'auto_reenabled' ? 'bg-green-50 text-green-700 border-green-200' :
                      'bg-blue-50 text-blue-700 border-blue-200'
                    }>
                      {entry.action === 'auto_reenabled' ? 'Auto Re-enabled' : entry.action.replace('_', ' ')}
                    </Badge>
                    <span className="text-xs text-text-light">{formatTimestamp(entry.timestamp)}</span>
                  </div>
                  <p className="text-sm text-text-mid">{entry.reason}</p>
                  {entry.details?.days && (
                    <p className="text-xs text-text-light mt-1">Duration: {entry.details.days} days (until {new Date(entry.details.end_date).toLocaleDateString()})</p>
                  )}
                  <p className="text-xs text-text-light mt-1">
                    By: {entry.admin_email === 'system' ? 'System (auto)' : entry.admin_email}
                  </p>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== CREATE PROMO CODE MODAL ===== */}
      <Dialog open={createPromoOpen} onOpenChange={(v) => { setCreatePromoOpen(v); if (!v) { setEditingPromoId(null); setEditingPromoUsage(0); } }}>
        <DialogContent className="max-w-md" data-testid="create-promo-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">{editingPromoId ? 'Edit Promo Code' : 'Create Promo Code'}</DialogTitle>
            {editingPromoId && (
              <DialogDescription>Code name & discount type are locked. Adjust tier, percentage, plans, limit, expiry or label.</DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Code</Label>
              <div className="flex gap-2">
                <Input
                  value={newPromo.code}
                  onChange={(e) => setNewPromo(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                  placeholder="e.g. CMCVIP10"
                  className="rounded-xl font-mono"
                  disabled={Boolean(editingPromoId)}
                  data-testid="promo-code-input"
                />
                {!editingPromoId && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
                      let code = 'CMC';
                      for (let i = 0; i < 5; i++) code += chars[Math.floor(Math.random() * chars.length)];
                      setNewPromo(p => ({ ...p, code }));
                    }}
                    className="whitespace-nowrap rounded-xl"
                    data-testid="generate-code-btn"
                  >
                    Generate
                  </Button>
                )}
              </div>
            </div>
            <div>
              <Label>Discount Type</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => !editingPromoId && setNewPromo(p => ({ ...p, discount_type: 'free_trial' }))}
                  disabled={Boolean(editingPromoId)}
                  className={`rounded-xl border-2 px-3 py-2 text-sm font-medium transition-colors ${newPromo.discount_type === 'free_trial' ? 'border-teal bg-teal/10 text-teal-deep' : 'border-border-subtle text-text-mid hover:bg-cream/30'} ${editingPromoId ? 'opacity-60 cursor-not-allowed' : ''}`}
                  data-testid="promo-type-free-trial"
                >
                  Free Trial Access
                </button>
                <button
                  type="button"
                  onClick={() => !editingPromoId && setNewPromo(p => ({ ...p, discount_type: 'percent_off' }))}
                  disabled={Boolean(editingPromoId)}
                  className={`rounded-xl border-2 px-3 py-2 text-sm font-medium transition-colors ${newPromo.discount_type === 'percent_off' ? 'border-teal bg-teal/10 text-teal-deep' : 'border-border-subtle text-text-mid hover:bg-cream/30'} ${editingPromoId ? 'opacity-60 cursor-not-allowed' : ''}`}
                  data-testid="promo-type-percent-off"
                >
                  % Off at Checkout
                </button>
              </div>
            </div>

            {newPromo.discount_type === 'free_trial' ? (
              <>
                <div>
                  <Label>Tier</Label>
                  <Select value={newPromo.tier} onValueChange={(v) => setNewPromo(p => ({ ...p, tier: v }))}>
                    <SelectTrigger className="rounded-xl" data-testid="promo-tier-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="thrive">Thrive</SelectItem>
                      <SelectItem value="connect">Connect</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Duration (days)</Label>
                    <Input
                      type="number" min="1"
                      value={newPromo.duration_days}
                      onChange={(e) => setNewPromo(p => ({ ...p, duration_days: e.target.value }))}
                      className="rounded-xl"
                      data-testid="promo-duration-input"
                    />
                  </div>
                  <div>
                    <Label>Usage Limit</Label>
                    <Input
                      type="number" min="1"
                      value={newPromo.usage_limit}
                      onChange={(e) => setNewPromo(p => ({ ...p, usage_limit: e.target.value }))}
                      placeholder="Empty = unlimited"
                      className="rounded-xl"
                      data-testid="promo-limit-input"
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label>Discount Percentage</Label>
                  <div className="grid grid-cols-4 gap-2 mt-1">
                    {[10, 15, 20, 50].map(pct => (
                      <button
                        key={pct}
                        type="button"
                        onClick={() => setNewPromo(p => ({ ...p, percent_off: pct }))}
                        className={`rounded-xl border-2 px-3 py-2 text-sm font-bold transition-colors ${newPromo.percent_off === pct ? 'border-teal bg-teal text-white' : 'border-border-subtle text-text-mid hover:bg-cream/30'}`}
                        data-testid={`promo-percent-${pct}`}
                      >
                        {pct}%
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Applies to which plans</Label>
                  <p className="text-xs text-text-mid mb-2">Select one or more. First invoice only.</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: 'thrive_founding', label: 'Thrive — Founding ($24.99/mo)' },
                      { key: 'thrive_regular', label: 'Thrive — Regular ($49.99/mo)' },
                    ].map(p => {
                      const checked = newPromo.applicable_plans.includes(p.key);
                      return (
                        <label
                          key={p.key}
                          className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2 text-sm cursor-pointer transition-colors ${checked ? 'border-teal bg-teal/10 text-teal-deep' : 'border-border-subtle text-text-mid hover:bg-cream/30'}`}
                          data-testid={`promo-plan-${p.key}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => setNewPromo(prev => ({
                              ...prev,
                              applicable_plans: checked
                                ? prev.applicable_plans.filter(k => k !== p.key)
                                : [...prev.applicable_plans, p.key],
                            }))}
                            className="accent-teal"
                          />
                          {p.label}
                        </label>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <Label>Usage Limit</Label>
                  <Input
                    type="number" min="1"
                    value={newPromo.usage_limit}
                    onChange={(e) => setNewPromo(p => ({ ...p, usage_limit: e.target.value }))}
                    placeholder="Empty = unlimited"
                    className="rounded-xl"
                    data-testid="promo-limit-input"
                  />
                </div>
              </>
            )}
            <div>
              <Label>Expiry Date (optional)</Label>
              <Input
                type="date"
                value={newPromo.expiry_date}
                onChange={(e) => setNewPromo(p => ({ ...p, expiry_date: e.target.value }))}
                className="rounded-xl"
                data-testid="promo-expiry-input"
              />
            </div>
            <div>
              <Label>Label (optional)</Label>
              <Input
                value={newPromo.label}
                onChange={(e) => setNewPromo(p => ({ ...p, label: e.target.value }))}
                placeholder="e.g. Friends and Family Launch"
                className="rounded-xl"
                data-testid="promo-label-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setCreatePromoOpen(false); setEditingPromoId(null); setEditingPromoUsage(0); }}>Cancel</Button>
            <Button
              className="bg-teal hover:bg-teal-deep text-white rounded-full"
              data-testid="save-promo-btn"
              disabled={(!editingPromoId && !newPromo.code.trim()) || (newPromo.discount_type === 'percent_off' && newPromo.applicable_plans.length === 0)}
              onClick={async () => {
                try {
                  const usageLimitNum = newPromo.usage_limit ? parseInt(newPromo.usage_limit) : null;
                  if (editingPromoId && usageLimitNum != null && usageLimitNum < editingPromoUsage) {
                    toast.error(`Usage limit can't be below current usage (${editingPromoUsage})`);
                    return;
                  }
                  if (editingPromoId) {
                    const payload = {
                      usage_limit: usageLimitNum,
                      expiry_date: newPromo.expiry_date ? new Date(newPromo.expiry_date).toISOString() : null,
                      label: newPromo.label.trim(),
                      ...(newPromo.discount_type === 'free_trial'
                        ? { tier: newPromo.tier, duration_days: parseInt(newPromo.duration_days) || 10 }
                        : { percent_off: newPromo.percent_off, applicable_plans: newPromo.applicable_plans }),
                    };
                    await api.patch(`/promo/admin/codes/${editingPromoId}`, payload, { headers: getAuthHeader() });
                    toast.success(`Promo code ${newPromo.code} updated`);
                  } else {
                    const base = {
                      code: newPromo.code.trim(),
                      discount_type: newPromo.discount_type,
                      usage_limit: usageLimitNum,
                      expiry_date: newPromo.expiry_date ? new Date(newPromo.expiry_date).toISOString() : null,
                      label: newPromo.label.trim(),
                    };
                    const payload = newPromo.discount_type === 'free_trial'
                      ? { ...base, tier: newPromo.tier, duration_days: parseInt(newPromo.duration_days) || 10 }
                      : { ...base, percent_off: newPromo.percent_off, applicable_plans: newPromo.applicable_plans };
                    await api.post('/promo/admin/codes', payload, { headers: getAuthHeader() });
                    toast.success(`Promo code ${payload.code} created!`);
                  }
                  setCreatePromoOpen(false);
                  setEditingPromoId(null);
                  setEditingPromoUsage(0);
                  fetchData();
                } catch (err) {
                  toast.error(err.response?.data?.detail || (editingPromoId ? 'Failed to update promo code' : 'Failed to create promo code'));
                }
              }}
            >
              {editingPromoId ? 'Save Changes' : 'Create Code'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== EXTEND PROMO MODAL ===== */}
      <Dialog open={extendPromoModal.open} onOpenChange={(v) => !v && setExtendPromoModal({ open: false, member: null })}>
        <DialogContent className="max-w-sm" data-testid="extend-promo-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">Extend Promo Access</DialogTitle>
            <DialogDescription>
              Extend {extendPromoModal.member?.first_name}'s complimentary access
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Additional Days</Label>
            <Input
              type="number" min="1"
              value={extendDays}
              onChange={(e) => setExtendDays(e.target.value)}
              className="rounded-xl"
              data-testid="extend-days-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setExtendPromoModal({ open: false, member: null })}>Cancel</Button>
            <Button
              className="bg-teal hover:bg-teal-deep text-white rounded-full"
              data-testid="confirm-extend-btn"
              onClick={async () => {
                try {
                  await api.patch(`/promo/admin/members/${extendPromoModal.member.id}/extend`, { days: parseInt(extendDays) }, { headers: getAuthHeader() });
                  toast.success(`Extended by ${extendDays} days`);
                  setExtendPromoModal({ open: false, member: null });
                  fetchData();
                } catch { toast.error('Failed to extend'); }
              }}
            >
              Extend
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== CREATE QR CODE MODAL ===== */}

      {/* ===== EDIT EVENT MODAL ===== */}
      <Dialog open={editEventOpen} onOpenChange={setEditEventOpen}>
        <DialogContent className="max-w-lg" data-testid="edit-event-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">Edit Event</DialogTitle>
          </DialogHeader>
          {editingEvent && (
            <div className="space-y-4">
              <div>
                <Label>Event Title</Label>
                <Input
                  value={editingEvent.title}
                  onChange={(e) => setEditingEvent(ev => ({ ...ev, title: e.target.value }))}
                  className="rounded-xl"
                  data-testid="edit-event-title"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={editingEvent.description}
                  onChange={(e) => setEditingEvent(ev => ({ ...ev, description: e.target.value }))}
                  rows={3}
                  className="rounded-xl"
                  data-testid="edit-event-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Date & Time</Label>
                  <Input
                    type="datetime-local"
                    value={editingEvent.event_date}
                    onChange={(e) => setEditingEvent(ev => ({ ...ev, event_date: e.target.value }))}
                    className="rounded-xl"
                    data-testid="edit-event-date"
                  />
                </div>
                <div>
                  <Label>City</Label>
                  <Input
                    value={editingEvent.city}
                    onChange={(e) => setEditingEvent(ev => ({ ...ev, city: e.target.value }))}
                    className="rounded-xl"
                    data-testid="edit-event-city"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Location / Venue</Label>
                  <Input
                    value={editingEvent.location}
                    onChange={(e) => setEditingEvent(ev => ({ ...ev, location: e.target.value }))}
                    className="rounded-xl"
                    data-testid="edit-event-location"
                  />
                </div>
                <div>
                  <Label>Max Capacity</Label>
                  <Input
                    type="number"
                    value={editingEvent.max_capacity}
                    onChange={(e) => setEditingEvent(ev => ({ ...ev, max_capacity: parseInt(e.target.value) || 50 }))}
                    className="rounded-xl"
                    data-testid="edit-event-capacity"
                  />
                </div>
              </div>
              <div>
                <Label>Ticket Price ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00 = Free event"
                  value={editingEvent.price}
                  onChange={(e) => setEditingEvent(ev => ({ ...ev, price: e.target.value }))}
                  className="rounded-xl"
                  data-testid="edit-event-price"
                />
                <p className="text-xs text-text-mid mt-1">Set to 0 for a free event</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Event Type</Label>
                  <Select value={editingEvent.event_type || 'gathering'} onValueChange={(v) => setEditingEvent(ev => ({ ...ev, event_type: v }))}>
                    <SelectTrigger className="rounded-xl" data-testid="edit-event-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gathering">Gathering</SelectItem>
                      <SelectItem value="workshop">Workshop</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {(editingEvent.event_type === 'workshop') && (
                  <div>
                    <Label>Workshop Base Price ($)</Label>
                    <Input
                      type="number" step="0.01" min="0"
                      value={editingEvent.base_price ?? ''}
                      onChange={(e) => setEditingEvent(ev => ({ ...ev, base_price: e.target.value }))}
                      className="rounded-xl" data-testid="edit-event-base-price"
                    />
                  </div>
                )}
              </div>
              {(editingEvent.event_type === 'workshop') && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Host Name</Label>
                    <Input
                      value={editingEvent.host_name || ''}
                      onChange={(e) => setEditingEvent(ev => ({ ...ev, host_name: e.target.value }))}
                      className="rounded-xl" data-testid="edit-event-host-name"
                    />
                  </div>
                  <div>
                    <Label>Registration Opens (general)</Label>
                    <Input
                      type="datetime-local"
                      value={editingEvent.registration_opens_at || ''}
                      onChange={(e) => setEditingEvent(ev => ({ ...ev, registration_opens_at: e.target.value }))}
                      className="rounded-xl" data-testid="edit-event-reg-opens"
                    />
                  </div>
                </div>
              )}
              <div>
                <Label>What to Bring (optional)</Label>
                <Input
                  value={editingEvent.what_to_bring || ''}
                  onChange={(e) => setEditingEvent(ev => ({ ...ev, what_to_bring: e.target.value }))}
                  placeholder="e.g., yoga mat, water bottle"
                  className="rounded-xl" data-testid="edit-event-what-to-bring"
                />
              </div>
              {/* Safety & disclosure (Master Spec Phase 1) */}
              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm text-navy cursor-pointer">
                  <input type="checkbox" checked={!!editingEvent.children_welcome}
                    onChange={(e) => setEditingEvent(ev => ({ ...ev, children_welcome: e.target.checked }))}
                    className="w-4 h-4 accent-teal" data-testid="edit-event-children-welcome" />
                  Children welcome
                </label>
                <label className="flex items-center gap-2 text-sm text-navy cursor-pointer">
                  <input type="checkbox" checked={!!editingEvent.third_party_photography}
                    onChange={(e) => setEditingEvent(ev => ({ ...ev, third_party_photography: e.target.checked }))}
                    className="w-4 h-4 accent-teal" data-testid="edit-event-third-party-photo" />
                  Third-party photography
                </label>
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm text-navy cursor-pointer">
                  <input type="checkbox" checked={!!editingEvent.requires_health_disclaimer}
                    onChange={(e) => setEditingEvent(ev => ({ ...ev, requires_health_disclaimer: e.target.checked }))}
                    className="w-4 h-4 accent-teal" data-testid="edit-event-requires-health" />
                  Requires health disclaimer
                </label>
                {editingEvent.requires_health_disclaimer && (
                  <Textarea
                    value={editingEvent.health_disclaimer || ''}
                    onChange={(e) => setEditingEvent(ev => ({ ...ev, health_disclaimer: e.target.value }))}
                    placeholder="Health disclaimer shown on the event page before registration"
                    className="rounded-xl mt-2" rows={2} data-testid="edit-event-health-disclaimer"
                  />
                )}
              </div>
              <div>
                <Label>Waiver Template</Label>
                <Select
                  value={editingEvent.waiver_template || (editingEvent.event_type === 'workshop' ? 'workshop' : 'general')}
                  onValueChange={(v) => setEditingEvent(ev => ({ ...ev, waiver_template: v }))}
                >
                  <SelectTrigger className="rounded-xl" data-testid="edit-event-waiver-template">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General (gathering default)</SelectItem>
                    <SelectItem value="workshop">Workshop (adds health/activity language)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {editingEvent.event_type === 'workshop' && (
                <div className="bg-cream/60 rounded-xl p-3 space-y-3">
                  <p className="text-xs font-semibold text-navy uppercase tracking-wide">Practitioner credentials (admin-only, never public)</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      value={editingEvent.practitioner_license_type || ''}
                      onChange={(e) => setEditingEvent(ev => ({ ...ev, practitioner_license_type: e.target.value }))}
                      placeholder="License type"
                      className="rounded-xl" data-testid="edit-event-license-type"
                    />
                    <Input
                      value={editingEvent.practitioner_license_number || ''}
                      onChange={(e) => setEditingEvent(ev => ({ ...ev, practitioner_license_number: e.target.value }))}
                      placeholder="License number"
                      className="rounded-xl" data-testid="edit-event-license-number"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-navy cursor-pointer">
                    <input type="checkbox" checked={!!editingEvent.practitioner_insurance_verified}
                      onChange={(e) => setEditingEvent(ev => ({ ...ev, practitioner_insurance_verified: e.target.checked }))}
                      className="w-4 h-4 accent-teal" data-testid="edit-event-insurance-verified" />
                    Insurance verified
                  </label>
                </div>
              )}
              <div>
                <Label>Event Photos</Label>
                <EventPhotoUploader
                  value={editingEvent.photos || (editingEvent.photo_url ? [editingEvent.photo_url] : [])}
                  onChange={(arr) => setEditingEvent(ev => ({
                    ...ev,
                    photos: arr,
                    photo_url: arr[0] || '',
                  }))}
                  testIdPrefix="edit-event-photos"
                />
              </div>
            </div>
          )}
          <DialogFooter className="flex justify-between">
            <Button
              variant="outline"
              className="text-red-600 border-red-300 hover:bg-red-50 rounded-full"
              data-testid="delete-event-btn"
              onClick={() => { setEditEventOpen(false); handleDeleteEvent(editingEvent.id, editingEvent.title); }}
            >
              Delete Event
            </Button>
            <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setEditEventOpen(false)}>Cancel</Button>
            <Button
              className="bg-teal hover:bg-teal-deep text-white rounded-full"
              data-testid="save-event-edit-btn"
              onClick={async () => {
                try {
                  const payload = {
                    title: editingEvent.title,
                    description: editingEvent.description,
                    event_date: editingEvent.event_date ? new Date(editingEvent.event_date).toISOString() : undefined,
                    location: editingEvent.location,
                    city: editingEvent.city,
                    max_capacity: editingEvent.max_capacity,
                    price: parseFloat(editingEvent.price) || 0,
                    member_discount_price:
                      editingEvent.member_discount_price === '' || editingEvent.member_discount_price == null
                        ? null
                        : parseFloat(editingEvent.member_discount_price),
                    photo_url: editingEvent.photo_url || null,
                    photos: editingEvent.photos || [],
                    event_type: editingEvent.event_type || 'gathering',
                    what_to_bring: editingEvent.what_to_bring || null,
                    waiver_template: editingEvent.waiver_template || (editingEvent.event_type === 'workshop' ? 'workshop' : 'general'),
                    children_welcome: !!editingEvent.children_welcome,
                    requires_health_disclaimer: !!editingEvent.requires_health_disclaimer,
                    health_disclaimer: editingEvent.requires_health_disclaimer ? (editingEvent.health_disclaimer || null) : null,
                    third_party_photography: !!editingEvent.third_party_photography,
                    practitioner_license_type: editingEvent.practitioner_license_type || null,
                    practitioner_license_number: editingEvent.practitioner_license_number || null,
                    practitioner_insurance_verified: !!editingEvent.practitioner_insurance_verified,
                  };
                  if (payload.event_type === 'workshop') {
                    payload.base_price = editingEvent.base_price === '' || editingEvent.base_price == null ? 0 : parseFloat(editingEvent.base_price);
                    payload.host_name = editingEvent.host_name || null;
                    if (editingEvent.registration_opens_at) payload.registration_opens_at = new Date(editingEvent.registration_opens_at).toISOString();
                  }
                  await api.patch(`/admin/events/${editingEvent.id}`, payload, { headers: getAuthHeader() });
                  toast.success('Event updated!');
                  setEditEventOpen(false);
                  fetchData();
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to update event');
                }
              }}
            >
              Save Changes
            </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== CREATE QR CODE MODAL ===== */}
      <Dialog open={createQrOpen} onOpenChange={setCreateQrOpen}>
        <DialogContent className="max-w-md" data-testid="create-qr-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">Create QR Code</DialogTitle>
            <DialogDescription>Create a trackable QR code for print marketing</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Campaign Name *</Label>
              <Input
                value={newQr.label}
                onChange={(e) => setNewQr(q => ({ ...q, label: e.target.value }))}
                placeholder="e.g. Equinox Beverly Hills"
                className="rounded-xl"
                data-testid="qr-label-input"
              />
            </div>
            <div>
              <Label>Location / Address</Label>
              <Input
                value={newQr.location}
                onChange={(e) => setNewQr(q => ({ ...q, location: e.target.value }))}
                placeholder="e.g. 9601 Wilshire Blvd, Beverly Hills"
                className="rounded-xl"
                data-testid="qr-location-input"
              />
            </div>
            <div>
              <Label>Tracking Code (auto-generated if blank)</Label>
              <Input
                value={newQr.ref_code}
                onChange={(e) => setNewQr(q => ({ ...q, ref_code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }))}
                placeholder="e.g. equinox_bh"
                className="rounded-xl font-mono"
                data-testid="qr-ref-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateQrOpen(false)}>Cancel</Button>
            <Button
              className="bg-teal hover:bg-teal-deep text-white rounded-full"
              data-testid="save-qr-btn"
              disabled={!newQr.label.trim()}
              onClick={async () => {
                try {
                  await api.post('/admin/qr-codes', {
                    label: newQr.label.trim(),
                    location: newQr.location.trim(),
                    ref_code: newQr.ref_code.trim() || undefined,
                  }, { headers: getAuthHeader() });
                  toast.success('QR code created!');
                  setCreateQrOpen(false);
                  fetchData();
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to create QR code');
                }
              }}
            >
              Create QR Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== QR SIGNUPS MODAL ===== */}
      <Dialog open={qrSignupsModal.open} onOpenChange={(v) => !v && setQrSignupsModal({ open: false, qr: null, members: [] })}>
        <DialogContent className="max-w-lg" data-testid="qr-signups-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">
              Signups — {qrSignupsModal.qr?.label}
            </DialogTitle>
            <DialogDescription>
              {qrSignupsModal.members.length} member{qrSignupsModal.members.length !== 1 ? 's' : ''} signed up via this QR code
            </DialogDescription>
          </DialogHeader>
          {qrSignupsModal.members.length === 0 ? (
            <p className="text-text-mid text-sm text-center py-6">No signups yet from this QR code.</p>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Joined</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {qrSignupsModal.members.map(m => (
                    <TableRow key={m.id}>
                      <TableCell className="font-medium text-navy">{m.first_name} {m.last_name}</TableCell>
                      <TableCell className="text-sm text-text-mid">{m.email}</TableCell>
                      <TableCell><Badge className="bg-seafoam/30 text-navy text-xs">{m.membership_tier || 'free'}</Badge></TableCell>
                      <TableCell className="text-sm text-text-light">{m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Apply Promo Code Modal */}
      <Dialog open={promoModal.open} onOpenChange={(open) => { if (!open) { setPromoModal({ open: false, member: null }); setMemberPromoCode(''); } }}>
        <DialogContent className="max-w-md" data-testid="apply-promo-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">Apply Promo Code</DialogTitle>
            <DialogDescription>
              Apply a promo code to {promoModal.member?.first_name} {promoModal.member?.last_name}'s account.
              This will change their membership tier for the promo duration.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Current Tier</Label>
              <Badge className={tierBadge[promoModal.member?.membership_tier] || 'bg-gray-100 text-gray-600'}>
                {tierLabel(promoModal.member?.membership_tier || 'free')}
              </Badge>
              {promoModal.member?.promo_status === 'active' && (
                <span className="text-xs text-blue-600 ml-2">Active promo: {promoModal.member?.promo_code}</span>
              )}
            </div>
            <div>
              <Label>Promo Code</Label>
              <Input
                value={memberPromoCode}
                onChange={(e) => setMemberPromoCode(e.target.value.toUpperCase())}
                placeholder="e.g. CMCVIP10"
                className="rounded-xl font-mono"
                data-testid="promo-code-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => setPromoModal({ open: false, member: null })}>
                Cancel
              </Button>
              <Button
                className="bg-teal hover:bg-teal-deep rounded-full"
                disabled={promoLoading || !memberPromoCode.trim()}
                onClick={handleApplyPromo}
                data-testid="apply-promo-submit"
              >
                {promoLoading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Tag size={16} className="mr-2" />}
                Apply Promo
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Password Reset Modal */}
      <Dialog open={resetModal.open} onOpenChange={(open) => { if (!open) { setResetModal({ open: false, member: null }); setResetCustomEmail(''); setResetEmailChoice('original'); } }}>
        <DialogContent className="max-w-md" data-testid="reset-password-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">Send Password Reset</DialogTitle>
            <DialogDescription>
              Send a password reset link for {resetModal.member?.first_name} {resetModal.member?.last_name}'s account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Option 1: Email on file */}
            <label
              className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                resetEmailChoice === 'original' ? 'border-teal bg-teal/5' : 'border-border-subtle hover:bg-cream/30'
              }`}
              data-testid="reset-email-original"
            >
              <input
                type="radio"
                name="resetEmail"
                checked={resetEmailChoice === 'original'}
                onChange={() => setResetEmailChoice('original')}
                className="mt-1 accent-teal"
              />
              <div>
                <p className="text-sm font-medium text-navy">Send to email on file</p>
                <p className="text-sm text-teal font-mono mt-0.5" data-testid="member-email-display">
                  {resetModal.member?.email}
                </p>
              </div>
            </label>

            {/* Option 2: Custom email */}
            <label
              className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                resetEmailChoice === 'custom' ? 'border-teal bg-teal/5' : 'border-border-subtle hover:bg-cream/30'
              }`}
              data-testid="reset-email-custom"
            >
              <input
                type="radio"
                name="resetEmail"
                checked={resetEmailChoice === 'custom'}
                onChange={() => setResetEmailChoice('custom')}
                className="mt-1 accent-teal"
              />
              <div className="flex-1">
                <p className="text-sm font-medium text-navy">Send to a different email</p>
                {resetEmailChoice === 'custom' && (
                  <Input
                    type="email"
                    value={resetCustomEmail}
                    onChange={(e) => setResetCustomEmail(e.target.value)}
                    placeholder="Enter new email address"
                    className="rounded-xl mt-2"
                    autoFocus
                    data-testid="reset-custom-email-input"
                  />
                )}
              </div>
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" className="rounded-full" onClick={() => setResetModal({ open: false, member: null })}>
                Cancel
              </Button>
              <Button
                className="bg-teal hover:bg-teal-deep rounded-full"
                disabled={resetLoading || (resetEmailChoice === 'custom' && !resetCustomEmail.trim())}
                onClick={handleSendResetLink}
                data-testid="send-reset-submit"
              >
                {resetLoading ? <Loader2 size={16} className="animate-spin mr-2" /> : <KeyRound size={16} className="mr-2" />}
                Send Reset Link
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Register Guest Modal */}
      <Dialog open={guestModal.open} onOpenChange={(open) => { if (!open) { setGuestModal({ open: false, eventId: null, eventTitle: '' }); setGuestName(''); setGuestEmail(''); } }}>
        <DialogContent className="max-w-md" data-testid="register-guest-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">Register Guest Attendee</DialogTitle>
            <DialogDescription>
              Manually register an attendee for "{guestModal.eventTitle}". They don't need to be a member.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Guest Name *</Label>
              <Input value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Full name" className="rounded-xl" data-testid="guest-name-input" />
            </div>
            <div>
              <Label>Email Address (optional — for calendar invite)</Label>
              <Input type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} placeholder="guest@example.com" className="rounded-xl" data-testid="guest-email-input" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => setGuestModal({ open: false, eventId: null, eventTitle: '' })}>Cancel</Button>
              <Button className="bg-navy hover:bg-navy/90 rounded-full" disabled={guestLoading || !guestName.trim()} onClick={handleRegisterGuest} data-testid="register-guest-submit">
                {guestLoading ? <Loader2 size={16} className="animate-spin mr-2" /> : <UserCheck size={16} className="mr-2" />}Register
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stat Detail Modal */}
      <Dialog open={statDetail.open} onOpenChange={(open) => { if (!open) setStatDetail({ open: false, title: '', members: [], loading: false }); }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="stat-detail-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">{statDetail.title}</DialogTitle>
            <DialogDescription>{statDetail.members.length} member{statDetail.members.length !== 1 ? 's' : ''}</DialogDescription>
          </DialogHeader>
          {statDetail.loading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-teal" size={32} /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Joined</TableHead>
                  {statDetail.title === 'Incomplete Onboarding' && <TableHead>Action</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {statDetail.members.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell className="font-medium text-navy">{m.first_name} {m.last_name}</TableCell>
                    <TableCell className="text-sm">{m.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs capitalize">{m.membership_tier || 'free'}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-text-mid">
                      {m.created_at ? new Date(m.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                    </TableCell>
                    {statDetail.title === 'Incomplete Onboarding' && (
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full text-xs border-teal text-teal hover:bg-teal hover:text-white"
                          data-testid={`message-member-${m.id}`}
                          onClick={async () => {
                            try {
                              await api.post('/messages/send', {
                                recipient_id: m.id,
                                content: `Hi ${m.first_name}! We noticed you haven't completed your onboarding yet. Complete it to get matched with amazing moms in your area! Let us know if you need any help.`
                              }, { headers: getAuthHeader() });
                              toast.success(`Message sent to ${m.first_name}`);
                            } catch { toast.error('Failed to send message'); }
                          }}
                        >
                          Send Nudge
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {statDetail.members.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-text-mid">No members found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      {/* Help Reply Modal */}
      <Dialog
        open={helpReplyModal.open}
        onOpenChange={(open) => { if (!open) setHelpReplyModal({ open: false, request: null }); }}
      >
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="help-reply-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">
              {helpReplyModal.request?.subject || 'Help Request'}
            </DialogTitle>
            <DialogDescription>
              {helpReplyModal.request && (
                <>Conversation with <strong>{helpReplyModal.request.user_name}</strong> ({helpReplyModal.request.user_email})</>
              )}
            </DialogDescription>
          </DialogHeader>
          {helpReplyModal.request && (
            <div className="space-y-2 max-h-[45vh] overflow-y-auto mb-3" data-testid="admin-help-thread-list">
              {(helpReplyModal.request.replies && helpReplyModal.request.replies.length > 0
                ? helpReplyModal.request.replies
                : [{
                    id: 'legacy',
                    from_admin: false,
                    author_name: helpReplyModal.request.user_name,
                    message: helpReplyModal.request.message,
                    created_at: helpReplyModal.request.created_at,
                  }]
              ).map((m) => (
                <div
                  key={m.id}
                  className={`rounded-xl p-3 text-sm ${m.from_admin ? 'bg-teal/5 border-l-4 border-teal' : 'bg-beige/40'}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-semibold ${m.from_admin ? 'text-teal' : 'text-navy'}`}>
                      {m.from_admin ? `Admin${m.author_name ? ' · ' + m.author_name : ''}` : (m.author_name || 'Member')}
                    </span>
                    <span className="text-[10px] text-text-mid">
                      {m.created_at ? new Date(m.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                    </span>
                  </div>
                  <p className="whitespace-pre-wrap">{m.message}</p>
                </div>
              ))}
            </div>
          )}
          <Textarea
            value={helpReplyText}
            onChange={(e) => setHelpReplyText(e.target.value)}
            placeholder="Type your reply..."
            rows={4}
            data-testid="help-reply-input"
            maxLength={3000}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setHelpReplyModal({ open: false, request: null })}
              className="rounded-full"
            >
              Close
            </Button>
            <Button
              onClick={submitHelpReply}
              disabled={helpReplySending || !helpReplyText.trim()}
              className="bg-teal hover:bg-teal-deep text-white rounded-full"
              data-testid="help-reply-send-btn"
            >
              {helpReplySending ? <Loader2 size={16} className="animate-spin mr-2" /> : <Send size={14} className="mr-2" />}
              Send Reply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Member Modal */}
      <Dialog
        open={deleteMemberModal.open}
        onOpenChange={(open) => { if (!open) setDeleteMemberModal({ open: false, member: null }); }}
      >
        <DialogContent className="max-w-lg" data-testid="delete-member-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-red-600">Delete Member</DialogTitle>
            <DialogDescription>
              {deleteMemberModal.member && (
                <>
                  You are about to delete <strong>{deleteMemberModal.member.first_name} {deleteMemberModal.member.last_name}</strong> ({deleteMemberModal.member.email}).
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm">
              <p className="font-semibold text-red-800 mb-1">Choose deletion type</p>
              <div className="space-y-2 mt-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="delete-action"
                    value="soft"
                    checked={deleteAction === 'soft'}
                    onChange={() => setDeleteAction('soft')}
                    data-testid="delete-soft-radio"
                    className="mt-1"
                  />
                  <div>
                    <p className="font-medium text-navy">Soft delete <span className="text-xs font-normal text-text-mid">(recommended)</span></p>
                    <p className="text-xs text-text-mid">Hide the account and block login. Can be restored later.</p>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="delete-action"
                    value="purge"
                    checked={deleteAction === 'purge'}
                    onChange={() => setDeleteAction('purge')}
                    data-testid="delete-purge-radio"
                    className="mt-1"
                  />
                  <div>
                    <p className="font-medium text-red-700">Purge permanently</p>
                    <p className="text-xs text-text-mid">Remove the account and all associated data (messages, RSVPs, matches, etc.). Cannot be undone.</p>
                  </div>
                </label>
              </div>
            </div>

            {deleteAction === 'soft' && (
              <div>
                <Label className="text-xs font-semibold">Reason (optional)</Label>
                <Input
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="e.g., Test account"
                  className="rounded-xl"
                  data-testid="delete-reason-input"
                />
              </div>
            )}

            <div>
              <Label className="text-xs font-semibold">
                Type <code className="bg-cream/70 px-1 rounded text-red-700">{deleteMemberModal.member?.email}</code> to confirm
              </Label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={deleteMemberModal.member?.email}
                className="rounded-xl font-mono"
                data-testid="delete-confirm-input"
                autoComplete="off"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteMemberModal({ open: false, member: null })} className="rounded-full">
              Cancel
            </Button>
            <Button
              onClick={submitDeleteMember}
              disabled={deleteSubmitting || deleteConfirmText !== deleteMemberModal.member?.email}
              className={(deleteAction === 'purge' ? 'bg-red-700 hover:bg-red-800' : 'bg-red-500 hover:bg-red-600') + ' text-white rounded-full'}
              data-testid="delete-confirm-btn"
            >
              {deleteSubmitting ? <Loader2 size={16} className="animate-spin mr-2" /> : <Trash2 size={14} className="mr-2" />}
              {deleteAction === 'purge' ? 'Purge Permanently' : 'Soft Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Modal */}
      <Dialog
        open={bulkDeleteModal}
        onOpenChange={(open) => {
          setBulkDeleteModal(open);
          if (!open) { setBulkPattern(''); setBulkConfirm(''); setBulkMatches(null); setBulkAction('soft'); }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="bulk-delete-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-red-600">Bulk Delete Members</DialogTitle>
            <DialogDescription>
              Use an email wildcard pattern (e.g., <code>*@example.com</code> or <code>test*@*</code>). Admin and super admin accounts are always protected.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={bulkPattern}
                onChange={(e) => { setBulkPattern(e.target.value); setBulkMatches(null); setBulkConfirm(''); }}
                placeholder="test*@example.com"
                className="rounded-xl font-mono"
                data-testid="bulk-pattern-input"
              />
              <Button
                onClick={runBulkPreview}
                disabled={bulkLoading || bulkPattern.trim().length < 3}
                className="bg-navy hover:bg-navy/80 text-white rounded-full"
                data-testid="bulk-preview-btn"
              >
                {bulkLoading && !bulkMatches ? <Loader2 size={16} className="animate-spin mr-2" /> : <Search size={14} className="mr-2" />}
                Preview Matches
              </Button>
            </div>

            {bulkMatches && (
              <div className="bg-cream/40 rounded-xl p-3 text-sm" data-testid="bulk-preview-results">
                <p className="font-semibold text-navy mb-2">
                  {bulkMatches.total} match{bulkMatches.total !== 1 ? 'es' : ''} ({bulkMatches.eligible} eligible to delete)
                </p>
                <div className="max-h-56 overflow-y-auto space-y-1">
                  {bulkMatches.matches.map((m) => (
                    <div key={m.id} className="flex items-center justify-between text-xs bg-white rounded-lg px-2 py-1.5">
                      <span className="font-mono">{m.email}</span>
                      {m.eligible ? (
                        <Badge className="bg-red-100 text-red-700 text-[10px]">will delete</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] text-text-mid">skip: {m.ineligible_reason}</Badge>
                      )}
                    </div>
                  ))}
                  {bulkMatches.matches.length === 0 && (
                    <p className="text-text-mid italic text-xs">No members match this pattern.</p>
                  )}
                </div>
              </div>
            )}

            {bulkMatches && bulkMatches.eligible > 0 && (
              <>
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm">
                  <p className="font-semibold text-red-800 mb-1">Choose deletion type</p>
                  <div className="space-y-2 mt-2">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="radio" name="bulk-action" value="soft" checked={bulkAction === 'soft'} onChange={() => setBulkAction('soft')} data-testid="bulk-soft-radio" className="mt-1" />
                      <div>
                        <p className="font-medium text-navy">Soft delete all eligible</p>
                        <p className="text-xs text-text-mid">Hide from the app and block login. Restorable.</p>
                      </div>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="radio" name="bulk-action" value="purge" checked={bulkAction === 'purge'} onChange={() => setBulkAction('purge')} data-testid="bulk-purge-radio" className="mt-1" />
                      <div>
                        <p className="font-medium text-red-700">Purge permanently</p>
                        <p className="text-xs text-text-mid">Delete accounts and all associated data. Cannot be undone.</p>
                      </div>
                    </label>
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-semibold">
                    Type the pattern <code className="bg-cream/70 px-1 rounded text-red-700">{bulkPattern}</code> to confirm
                  </Label>
                  <Input
                    value={bulkConfirm}
                    onChange={(e) => setBulkConfirm(e.target.value)}
                    placeholder={bulkPattern}
                    className="rounded-xl font-mono"
                    data-testid="bulk-confirm-input"
                    autoComplete="off"
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteModal(false)} className="rounded-full">
              Cancel
            </Button>
            <Button
              onClick={submitBulkDelete}
              disabled={bulkLoading || !bulkMatches || bulkMatches.eligible === 0 || bulkConfirm !== bulkPattern}
              className={(bulkAction === 'purge' ? 'bg-red-700 hover:bg-red-800' : 'bg-red-500 hover:bg-red-600') + ' text-white rounded-full'}
              data-testid="bulk-delete-confirm-btn"
            >
              {bulkLoading && bulkMatches ? <Loader2 size={16} className="animate-spin mr-2" /> : <Trash2 size={14} className="mr-2" />}
              {bulkAction === 'purge' ? 'Purge Permanently' : 'Soft Delete All'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Purge All Deleted Modal */}
      <Dialog
        open={purgeAllModal}
        onOpenChange={(open) => { setPurgeAllModal(open); if (!open) setPurgeAllConfirm(''); }}
      >
        <DialogContent className="max-w-md" data-testid="purge-all-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-red-700">Purge ALL Deleted Accounts</DialogTitle>
            <DialogDescription>
              This will permanently delete all soft-deleted members and their associated data (messages, RSVPs, matches, etc.). This cannot be undone. Super admin is always protected.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs font-semibold">
              Type <code className="bg-cream/70 px-1 rounded text-red-700">PURGE ALL</code> to confirm
            </Label>
            <Input
              value={purgeAllConfirm}
              onChange={(e) => setPurgeAllConfirm(e.target.value)}
              placeholder="PURGE ALL"
              className="rounded-xl font-mono"
              data-testid="purge-all-confirm-input"
              autoComplete="off"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPurgeAllModal(false)} className="rounded-full">Cancel</Button>
            <Button
              onClick={submitPurgeAll}
              disabled={purgeAllLoading || purgeAllConfirm !== 'PURGE ALL'}
              className="bg-red-700 hover:bg-red-800 text-white rounded-full"
              data-testid="purge-all-confirm-btn"
            >
              {purgeAllLoading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Trash2 size={14} className="mr-2" />}
              Purge All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stripe Audit Modal */}
      <Dialog
        open={stripeAuditModal}
        onOpenChange={(open) => { setStripeAuditModal(open); if (!open) setStripeAuditData(null); }}
      >
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="stripe-audit-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">Stripe Subscription Audit</DialogTitle>
            <DialogDescription>
              Cross-reference live Stripe subscriptions against the user database.
            </DialogDescription>
          </DialogHeader>

          {stripeAuditLoading || !stripeAuditData ? (
            <div className="flex flex-col items-center justify-center py-10">
              <Loader2 size={28} className="animate-spin text-teal" />
              <p className="text-sm text-text-mid mt-3">Calling Stripe live API…</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Summary tiles */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="stripe-audit-summary">
                <div className="bg-cream/60 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-navy">{stripeAuditData.summary.stripe_subscriptions_scanned}</p>
                  <p className="text-[10px] text-text-mid uppercase tracking-wide">Stripe subs</p>
                </div>
                <div className="bg-cream/60 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-navy">{stripeAuditData.summary.users_scanned}</p>
                  <p className="text-[10px] text-text-mid uppercase tracking-wide">DB users</p>
                </div>
                <div className={`rounded-xl p-3 text-center ${stripeAuditData.summary.free_tier_with_active_sub > 0 ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                  <p className={`text-2xl font-bold ${stripeAuditData.summary.free_tier_with_active_sub > 0 ? 'text-red-700' : 'text-green-700'}`}>
                    {stripeAuditData.summary.free_tier_with_active_sub}
                  </p>
                  <p className="text-[10px] text-text-mid uppercase tracking-wide">Free + charged</p>
                </div>
                <div className={`rounded-xl p-3 text-center ${stripeAuditData.summary.orphan_subs > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
                  <p className={`text-2xl font-bold ${stripeAuditData.summary.orphan_subs > 0 ? 'text-amber-700' : 'text-green-700'}`}>
                    {stripeAuditData.summary.orphan_subs}
                  </p>
                  <p className="text-[10px] text-text-mid uppercase tracking-wide">Orphan subs</p>
                </div>
              </div>

              <p className="text-[11px] text-text-mid text-right">
                Scanned at {new Date(stripeAuditData.summary.scanned_at).toLocaleString()}
              </p>

              {/* Free tier with active sub — CRITICAL */}
              <div data-testid="audit-free-tier-section">
                <h4 className="font-semibold text-navy mb-2 flex items-center gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                  Free tier with active subscription
                  <span className="text-xs font-normal text-text-mid">({stripeAuditData.free_tier_with_active_sub.length})</span>
                </h4>
                {stripeAuditData.free_tier_with_active_sub.length === 0 ? (
                  <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                    ✅ No free-tier members are being charged. You're clean.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {stripeAuditData.free_tier_with_active_sub.map((r, i) => (
                      <div key={i} className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div className="flex-1">
                            <p className="font-semibold text-red-800">{r.email}</p>
                            <p className="text-text-mid">
                              Tier: <strong>{r.membership_tier}</strong> · Sub: <code>{r.stripe_subscription_id}</code> · {r.amount_cents ? `$${(r.amount_cents / 100).toFixed(2)}/period` : ''}
                            </p>
                            <p className="text-red-700 mt-1">Cancel below or open the member and click <strong>Unlink Payment</strong>.</p>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleCancelOrphanSub(r.stripe_subscription_id, r.email)}
                            className="bg-red-600 hover:bg-red-700 text-white rounded-full text-xs flex-shrink-0"
                            data-testid={`cancel-sub-${r.stripe_subscription_id}`}
                          >
                            Cancel in Stripe
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Orphan subs */}
              <div data-testid="audit-orphan-section">
                <h4 className="font-semibold text-navy mb-2 flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                  Orphan subscriptions
                  <span className="text-xs font-normal text-text-mid">({stripeAuditData.orphan_subs.length})</span>
                </h4>
                {stripeAuditData.orphan_subs.length === 0 ? (
                  <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                    ✅ No orphan subscriptions in Stripe.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-56 overflow-y-auto">
                    {stripeAuditData.orphan_subs.map((r, i) => (
                      <div key={i} className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div className="flex-1">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <p className="font-semibold text-amber-900">{r.stripe_customer_email || '(no email on Stripe customer)'}</p>
                              <Badge className="bg-amber-200 text-amber-900 text-[10px]">{r.stripe_status}</Badge>
                            </div>
                            <p className="text-text-mid">Sub: <code>{r.stripe_subscription_id}</code></p>
                            <p className="text-amber-800 mt-1">
                              {r.issue === 'no_matching_user'
                                ? 'No CaliMoms account exists for this Stripe customer. Cancel below or invite them to sign up.'
                                : 'Customer exists in DB but stripe_customer_id is not linked. Re-link via the member page or cancel here.'}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleCancelOrphanSub(r.stripe_subscription_id, r.stripe_customer_email)}
                            className="bg-red-600 hover:bg-red-700 text-white rounded-full text-xs flex-shrink-0"
                            data-testid={`cancel-orphan-sub-${r.stripe_subscription_id}`}
                          >
                            Cancel in Stripe
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Paid without sub */}
              <div data-testid="audit-paid-tier-section">
                <h4 className="font-semibold text-navy mb-2 flex items-center gap-2">
                  <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
                  Paid tier without active subscription
                  <span className="text-xs font-normal text-text-mid">({stripeAuditData.paid_tier_without_active_sub.length})</span>
                </h4>
                {stripeAuditData.paid_tier_without_active_sub.length === 0 ? (
                  <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                    ✅ All paid-tier members have an active subscription (or are on promo / paused).
                  </p>
                ) : (
                  <details className="bg-yellow-50 border border-yellow-200 rounded-lg" data-testid="audit-paid-tier-details">
                    <summary className="cursor-pointer px-3 py-2 text-xs text-yellow-900 font-semibold">
                      {stripeAuditData.paid_tier_without_active_sub.length} member{stripeAuditData.paid_tier_without_active_sub.length !== 1 ? 's' : ''} — likely admin/test or comped accounts. Click to expand.
                    </summary>
                    <div className="px-3 pb-3 space-y-1 max-h-40 overflow-y-auto">
                      {stripeAuditData.paid_tier_without_active_sub.map((r, i) => (
                        <p key={i} className="text-xs text-text-mid">
                          {r.email} · tier <strong>{r.membership_tier}</strong> · ps={r.payment_status || '—'}
                        </p>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setStripeAuditModal(false)} className="rounded-full">
              Close
            </Button>
            <Button
              onClick={runStripeAudit}
              disabled={stripeAuditLoading}
              className="bg-teal hover:bg-teal-deep text-white rounded-full"
              data-testid="stripe-audit-rerun-btn"
            >
              {stripeAuditLoading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              Re-run Audit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
